/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { authStore } from "../../../lib/api";
import { toast } from "../../../components/ui/sonner";
import { Button } from "../../../components/ui/button";
import TaskFilesModal from "../TaskFilesModal";
import QAModal from "../QAModal";
import { AgencyAdviceModal } from "../DesignAIModals";
import { useApp } from "../../../context/AppContext";
/* 【2026-10-29 时区口径统一】设计部一律按美国太平洋时间显示与输入。
   原来 import 的是 lib/date 的「浏览器本机时区」版本 + localTz/dayStartIso，
   中国同事和美国同事看同一条截止日会差一天；现在换成 usDate 固定洛杉矶实例，
   usDayStartIso 把「美国日历日」落成洛杉矶零点瞬间，谁来回看都是同一天。
   对应后端 design_module.PROJECT_TZ 与 tz_day_start 的同一次口径统一。 */
import { dateInputValue, fmtD, fmtMD, usDayStartIso, usToday } from "../../../lib/usDate";
/* 【2026-10-30】改期 prompt 的时区说明直接复用 UsDateHint 的同一个常量，
   避免「弹窗里一套话、提示条上另一套话」（2026-10-29 口径漂移的老毛病）。 */
import { US_DATE_TIP } from "./UsDateHint";
import { DS_COLOR, AGENCY_STATUSES, AG_ZH, AG_DONE, APPLIC, useDiscAgencyMap, reverseAgencyMap, useGridCols, discStatusLabel, isStageHiddenInProjects, isClosedProject, FILE_TYPE_ZH, projectOwnerOptions, ownerOptionLabel, DESIGN_UPLOAD_ACCEPT, TASK_TO_DISC, DISC_TO_TASK, REOPENABLE_STATUSES as REOPENABLE, doneDownstreamOf,
  STAGE_PHASE, STAGE_PHASE_LABEL, STAGE_PHASE_BADGE, stagePhasesOf, flowFrontierOf, flowOrderOf, stageTooEarlyOf,
  stageConsolidationOf, stageMembersOfConsolidation, canonicalStageKey, isStageClosedStatus, formatProgress } from "./meta";
import { stageStatusOfMembers, isStageClosed } from "./stageStatus";
import FileUploadDate, { uploadDateLabel } from "./FileUploadDate";
export { FILE_TYPE_ZH, TASK_TO_DISC, DISC_TO_TASK } from "./meta";
import { CheckCircle2, ChevronDown, ChevronRight, FolderOpen, Package, RotateCcw, ShieldCheck, FileCheck2, AlertTriangle, Loader2, Sparkles, Hammer, GanttChartSquare, CalendarClock, Layers, UserPlus, UploadCloud,
  MessageSquareText, FileSignature, Ruler, PenTool, UserCheck, FileText, Send, Hourglass, BadgeCheck, Building2, Columns3, ClipboardList, Handshake, Landmark, Paperclip, FileDown, ListChecks, MoveHorizontal, ArrowDownUp, Info } from "lucide-react";
import { downloadFile } from "../../../lib/fileDownload";

import { T, TB, TE, getLang } from "../../../lib/i18n";
import { showContractWarnings } from "../../../lib/contractWarn";
import { HandoffDialog } from "./HandoffDialog";
import { HandoffPermitField } from "./HandoffPermitField";
import { DesignConfirmDialog } from "./DesignConfirmDialog";

/* 英文键系统标签 → 中文（zh 模式显示；en 原样；es 走词典） */
export const STATUS_ZH = { "Not Started": "未开始", Ready: "就绪", "In Progress": "进行中", "Internal Review": "内部审核", Submitted: "已提交", "Awaiting Client": "等客户", "Awaiting External": "等外部", "Correction Required": "需整改", Blocked: "受阻", Approved: "已批准", Completed: "已完成", "Not Applicable": "不适用" };
export const WAIT_ZH = { "Client Waiting": "等客户", "City Waiting": "等市府", "Utility/Consultant Waiting": "等公用事业 / 顾问", "Paused by Company": "公司暂停", Blocked: "受阻" };
export const ROLE_ZH = { us_policy: "美国建筑设计师", cn_architect: "中国建筑设计师", design_manager: "设计经理", cn_drafter: "中国绘图员", pm: "项目经理", sales: "销售", admin: "管理员" };
const teStatus = (v) => TE(v, STATUS_ZH);
/* 阶段任务状态下拉的选项表（2026-09 起只保留 4 项）。
   2026-10-21 起前端已不再提供任何手选任务状态的入口（用户要求「这个选项不需要了，
   因为都是必传才能进行下一个步骤」），这两个导出仅作为历史口径保留：
   旧页面 / 自动化脚本仍可能引用，贸然删除会让它们编译失败，且状态键本身仍是后端
   STATUSES 的合法子集，不需要清理。 */
export const TASK_STATUS_OPTIONS = ["Not Applicable", "Submitted", "Correction Required", "Completed"];
export const taskStatusOptions = (cur) => (cur && !TASK_STATUS_OPTIONS.includes(cur) ? [cur, ...TASK_STATUS_OPTIONS] : TASK_STATUS_OPTIONS);
import { PreStagePanel, PRE_KEYS } from "./pre/PreFlow";
import { MediaLightbox, mediaKind } from "../../../components/MediaLightbox";
import { useContextMenu } from "../../../components/ContextMenu";
import { SubmittalPanel, STAGE_AGENCY } from "./submittals/SubmittalPanel";
import { CityReviewBlock } from "./CityReviewBlock";
import { LandSurveyBlock } from "./LandSurveyBlock";
import { errTextOf } from "../../../lib/detailText";
import { uploadChunked, uploadErrText, UPLOAD_MAX, appendUploadRef } from "../../../lib/upload";

/* City 审图（plan check）环节：只有这两个阶段会有政府退回意见 → 需要多轮整改。
   与后端 design_const.CORRECTION_STAGES 必须保持一致（改一处要两边一起改），
   否则会出现「前端不显示块但接口允许开轮」或反之的错位。 */
export const CITY_REVIEW_STAGES = ["planning_review", "building_review"];
/* 【2026-10-21】City 审图阶段的旧版「City 状态跟进 (Follow-up)」子任务不再渲染。
 *
 * 原因：这个功能已经整体由下方 CityReviewBlock（多轮整改）承接 —— 同样挂在
 * planning_review / building_review、同样回答「City 这次审得怎么样」，但新块有真正的
 * 状态机（轮次 / 通过 / 退回 + 图纸与回复函两个必交槽位 + 每轮顺延工期）。
 * 旧卡只是模板里写死的一条静态待办（gate=false、无必交文件），它用通用任务状态去
 * 手工模拟「审到第几轮」，并且与新块**零耦合**：新增整改轮次不会改它，关闭阶段也不会
 * 改它 —— 所以它长期停在「未开始 / 0%」，看着就像流程卡死在这里。
 *
 * 取舍：这里只**隐藏渲染**，不删数据。
 *   · 存量项目库里的任务记录、负责人、文件全部保留（历史追责数据不能丢）；
 *   · 模板行也保留，避免历史项目与新项目出现两种任务结构；
 *   · 阶段完成度已改为「无子任务时按阶段状态判定」（design_module.stage_completion），
 *     隐藏后不再出现 0% 卡死；
 *   · 关阶段会同步收口本阶段子任务（design_module.stage_close_core），
 *     该任务随阶段关闭自动完成，不会再被逾期扫描反复催。
 * 若将来要彻底移除，应连同后端模板行与存量数据迁移一起做，而不是只删前端这片。 */
const LEGACY_CITY_FOLLOWUP_STAGE_KEYS = ["planning_review", "building_review"];
const LEGACY_CITY_FOLLOWUP_TITLE = "City 状态跟进 (Follow-up)";
export const isLegacyCityFollowupTask = (stageKey, task) =>
  LEGACY_CITY_FOLLOWUP_STAGE_KEYS.includes(stageKey) && task?.title === LEGACY_CITY_FOLLOWUP_TITLE;
/** 过滤掉不再展示的旧版 City 跟进卡；其余子任务原样返回。 */
const visibleTasks = (stageKey, tasks) => (tasks || []).filter((t) => !isLegacyCityFollowupTask(stageKey, t));

/* 旧版子任务 → 它服务的那项 v3 产物（2026-10-21 用户反馈「详情和类别矩阵聊天框右侧都重复了」）。

   最终口径（用户逐条澄清后确定）：**保留任务卡，收起配对的产物卡。**
   用户原话：「应该保留上面那种…你保留反了」「两个边都是」「Planning Submission Set
   这个阶段也是保留上面的」「之前卡片本来就有这个功能啊 上传 → 送审 → 通过」。
   理由：任务卡本来就承载负责人 / 拖入即上传 / 文件与版本（逐版 Draft·Submitted·
   Approved）/ QA / 改期，是用户熟悉的主入口；产物卡只有「状态 + 文件数」两样信息。

   配对表已上移到 meta.js（TASK_TO_DISC / DISC_TO_TASK），配置弹窗也要用同一份；
   这里转出以保持既有引用（StageChatDrawer 等）不变。

   边界（必须按对处理，不能整体砍）：
     · 只有**本阶段确实渲染了这张配对任务卡**时，才收该产物卡；没有配对任务的产物
       （人工办理项如消防 / 土地 / 学区费，以及机构类）一律照常显示 —— 它们没有任务卡
       可以承载，砍掉就彻底失去入口（dev 库实测：2071 个产物里只有 665 个有配对任务）。
     · 产物侧的图纸与审核记录仍要能看：任务卡上给出「产物」摘要入口，
       把产物侧上传的版本与送审结果挂到任务卡上，不因收起而不见（历史留档红线）。 */
/** 本阶段哪些产物卡已被同阶段的任务卡承载（→ 这些产物卡不再单独渲染）。
 *
 *  判定「被承载」需同时满足：
 *    ① 该产物在本阶段挂载（discKeys 有它）；
 *    ② 本阶段确实有一张标题能被 TASK_TO_DISC 识别、且不是旧版 City 跟进的任务卡。
 *  第二条是关键：历史项目里阶段可能只剩产物、任务已被清理（或根本没生成），
 *  此时不能把产物卡也收起来，否则这一项在页面上彻底消失。 */
export const carriedDiscKeys = (stageKey, tasks, discKeys) => {
  const out = new Set();
  if (!discKeys || !discKeys.size) return out;
  /* stageKey 允许传「逗号分隔的多个阶段」：P+B 合并节点要把**组内每个成员阶段**
     的旧版 City 跟进卡都过滤掉，否则审图节点里会冒出两张早已被 CityReviewBlock
     取代的僵尸卡。用逗号串而不是数组，是为了让调用方的 useMemo 依赖保持稳定
     （数组每次渲染都是新引用，会让 memo 每次都重算）。 */
  const keys = String(stageKey || "").split(",").filter(Boolean);
  keys.forEach((k) => visibleTasks(k, tasks).forEach((t) => {
    const dk = TASK_TO_DISC[t?.title];
    if (dk && discKeys.has(dk)) out.add(dk);
  }));
  return out;
};
/** 过滤出仍需要单独成卡的任务：只剔除被 CityReviewBlock 取代的旧版 City 跟进卡。
 *  配对任务现在**全部保留**（用户要求以任务卡为准），去重发生在产物侧（见 carriedDiscKeys）。 */
const stageTasksToRender = (stageKey, tasks) => visibleTasks(stageKey, tasks);

/* 阶段角标与展开内容共用同一份卡片清单。产物中的机构行和已由任务卡承载的
   产物不另计；专项面板、审图整改和交图邮件是独立区块，不在这三类卡片里。 */
export const stageCardContents = (stage, node, agencies, agToDisc) => {
  const memberKeys = stage.merged ? stage.stages.map((m) => m.key) : [stage.key];
  const disciplines = node?.disciplines || [];
  const discKeys = new Set(disciplines.map((disc) => disc.key));
  const carried = carriedDiscKeys(memberKeys.join(","), stage.tasks || [], discKeys);
  const stageAgencies = (agencies || [])
    .filter((agency) => (node?.agencies || []).some((linked) => linked.id === agency.id))
    .map((agency) => ({ ...agency, dueDate: stage.dueDate }));
  return {
    memberKeys,
    shownTasks: stageTasksToRender(stage.key, stage.tasks),
    shownDiscs: disciplines.filter((disc) => !carried.has(disc.key)),
    carriedDiscs: disciplines.filter((disc) => carried.has(disc.key)),
    agencies: stageAgencies,
    restAgs: stageAgencies.filter((agency) => !(agToDisc[agency.type] && discKeys.has(agToDisc[agency.type]))),
  };
};
/* Land Survey 可选块只挂方案设计阶段（与 design_const.LAND_SURVEY_STAGE 保持一致）：
   这是业主拿到第一版总平面 / 平面方案的阶段，测绘报告正是这一步的外部前置输入。 */
export const LAND_SURVEY_STAGES = ["schematic"];

/* 细粒度状态配色（12 态）。2026-10-23 起**不再用于阶段状态**——
   阶段那边统一走 meta.js 的 STAGE_PHASE_BADGE 三态（未开始 / 进行中 / 已完成）。
   这里保留给机构办理行与子任务行：它们的 12 态是业务上真实要区分的
   （等市府 / 需整改 / 等外部），收敛成三态会丢掉「卡在谁那里」的信息。 */
const STATUS_BADGE = (s) => ["Completed", "Approved", "Not Applicable"].includes(s) ? "bg-emerald-100 text-emerald-700" : ["Blocked", "Correction Required"].includes(s) ? "bg-red-100 text-red-700"
  : ["Awaiting Client", "Awaiting External", "Submitted"].includes(s) ? "bg-amber-100 text-amber-700" : ["In Progress", "Internal Review"].includes(s) ? "bg-sky-100 text-sky-700" : ["Ready"].includes(s) ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500";
export const STAGE_ICON = { pre_consult: MessageSquareText, contract: FileSignature, site_measure: Ruler, schematic: PenTool, owner_signoff: UserCheck, planning_set: Layers, planning_paperwork: FileText, planning_submit: Send, planning_review: Hourglass,
  planning_approval: BadgeCheck, building_arch: Building2, structural_t24: Columns3, building_paperwork: ClipboardList, building_submit: Send, building_review: Hourglass, building_approval: ShieldCheck, permit_handoff: Handshake };
export const STAGE_SHORT = { pre_consult: "前期咨询", contract: "合同签署", site_measure: "现场测量", schematic: "方案设计", owner_signoff: "业主签字", planning_set: "Planning 图", planning_paperwork: "Planning 材料", planning_submit: "Planning 提交", planning_review: "Planning 审",
  planning_approval: "Planning 批+二款", building_arch: "Building 图", structural_t24: "结构+T24", building_paperwork: "Building 材料", building_submit: "Building 提交", building_review: "Building 审", building_approval: "Building 批+Permit", permit_handoff: "三款+移交",
  pb_set: "P+B 图纸", pb_paperwork: "P+B 材料", pb_submit: "P+B 同步提交", pb_review: "P+B 同步审查", pb_approval: "P+B 批+Permit" };
/* 矩阵表头空间有限：主名称保持短，合并说明走第二行，完整说明仍在 title。 */
export const STAGE_SHORT_NOTE = { building_arch: "含结构 / T24" };
/* 审批模式 concurrent（Planning + Building 同步提交同步审查）：显示上把对应阶段合并为一个节点；底层阶段 / 子任务不变、仍需全部完成 */
export const APPROVAL_MODE_ZH = { sequential: "分开提交：先 Planning，批准后再 Building", concurrent: "同步提交：Planning + Building 同时提交、同步审查" };
export const CONCURRENT_GROUPS = [
  { key: "pb_set", name: "Planning + Building 图纸（含结构 / T24）", keys: ["planning_set", "building_arch", "structural_t24"], icon: Layers },
  { key: "pb_submit", name: "Planning + Building 同步提交", keys: ["planning_submit", "building_submit"], icon: Send },
  { key: "pb_review", name: "Planning + Building 同步审查", keys: ["planning_review", "building_review"], icon: Hourglass },
  { key: "pb_paperwork", name: "Planning + Building 材料", keys: ["planning_paperwork", "building_paperwork"], icon: FileText },
  { key: "pb_approval", name: "Planning + Building 批准 + Permit（二款）", keys: ["planning_approval", "building_approval"], icon: ShieldCheck },
];
/* CRM 设计看板等复用方拿不到 /design/v3/meta，但仍需按当前流程排序。
   这里只补顺序兜底；阶段是否存在仍以项目实际 stages 为准。 */
const STAGE_ORDER_FALLBACK = [
  "pre_consult", "contract", "site_measure", "schematic", "owner_signoff",
  "planning_set", "planning_paperwork", "planning_submit", "planning_review", "planning_approval",
  "building_arch", "building_submit", "building_review", "building_paperwork", "building_approval",
  "permit_handoff",
];
const ACTIVE_ST = ["Ready", "In Progress", "Internal Review", "Submitted", "Awaiting Client", "Awaiting External"];

/* 【2026-10-22 P+B 合并】把「一个节点」解析成它覆盖的底层阶段清单。

   为什么需要这个函数：合并视图有三个入口（详情阶段卡、矩阵格子、阶段会话抽屉），
   三处都必须按**同一套规则**判断「这个 key 到底代表一个阶段还是一个组」，否则会出现
   详情显示 9 个节点、矩阵还画 15 格、抽屉打开又只剩一半产物的错位。

   规则（顺序很重要）：
     1. 传进来的是合并节点 key（pb_xxx）→ 永远返回整组：调用方已经明确表达了「我要这个合并节点」；
     2. 传进来的是单个阶段 key 且项目是 concurrent → 返回它所在的整组：单阶段入口
        （详情深链、旧的收藏链接）在 P+B 项目里也要落到合并视图，否则「一个图」又被拆成两半；
     3. 传进来的是单个阶段 key 且项目是 sequential → 原样返回：分开提交的项目行为完全不变；
     4. 不在任何组里的阶段（现场测量 / 方案设计 / 业主签字 / 三款+移交）→ 原样返回。

   返回的数组保持 CONCURRENT_GROUPS 里的书写顺序，第一个元素即「主阶段」——
   后端整改轮次、评论写入、City 结论附件都只落到主阶段上，顺序不能随手排。 */
export const concurrentGroupOf = (key) =>
  CONCURRENT_GROUPS.find((g) => g.key === key || g.keys.includes(key)) || null;
export const concurrentMemberKeys = (stageKey, mode) => {
  const g = concurrentGroupOf(stageKey);
  if (!g) return [stageKey];
  if (g.key === stageKey) return g.keys.slice();
  return mode === "concurrent" ? g.keys.slice() : [stageKey];
};
/* 当前阶段入口对应的最终显示组：P+B 优先（它包含更外层节点），否则使用固定合并组。
   例如 concurrent 项目的 building_arch 应打开 pb_set；sequential 项目则打开
   Building 图（building_arch + structural_t24）。 */
export const stageMergeGroupOf = (stageKey, mode = "sequential") => {
  const concurrent = concurrentGroupOf(stageKey);
  if (concurrent && (mode === "concurrent" || concurrent.key === stageKey)) return concurrent;
  return stageConsolidationOf(stageKey) || concurrent;
};
export const stageMemberKeys = (stageKey, mode) => {
  const concurrent = concurrentGroupOf(stageKey);
  if (concurrent) {
    if (concurrent.key === stageKey || mode === "concurrent") return concurrent.keys.slice();
    if (stageConsolidationOf(stageKey)) return stageMembersOfConsolidation(stageKey);
    return [stageKey];
  }
  return stageMembersOfConsolidation(stageKey);
};

/* 卡片表面色（2026-10-18 样式优化）。
   去掉原来的单侧色条（border-l-4）：单侧粗色条是生成式 UI 的典型套路，而且和「卡片底色 + 状态药丸」
   三重复表达同一件事。现在一个状态只给一个信号：全边 hairline 边框 + 极浅表面色 + 状态点 + 药丸。
   已完成/已批准 = 浅绿底；整改/卡住 = 浅红底；已提交/审图 = 浅蓝底；其余一律白底 —— 让中性色承载重量，
   只有"完成"和"出问题"这两种需要一眼扫到的状态才染色。 */
const DISC_TONE = (st) => {
  const done = ["approved", "done", "exempt"].includes(st);
  const bad = ["correction", "stuck"].includes(st);
  const wait = ["submitted", "in_plan_check"].includes(st);
  const act = ["working", "preparing", "requested", "waiting_site_measure"].includes(st);
  return {
    done,
    card: done ? "border-emerald-200/80 bg-emerald-50/60" : bad ? "border-red-200/80 bg-red-50/50" : wait ? "border-violet-200/70 bg-violet-50/50" : "border-slate-200 bg-white",
    dot: done ? "bg-emerald-500" : bad ? "bg-red-500" : wait ? "bg-violet-500" : act ? "bg-indigo-400" : "bg-slate-300",
  };
};
const TASK_TONE = (st, over) => {
  const bad = over || ["Blocked", "Correction Required"].includes(st);
  const done = ["Completed", "Approved", "Not Applicable"].includes(st);
  const act = ["In Progress", "Internal Review", "Submitted", "Awaiting Client", "Awaiting External"].includes(st);
  return {
    done,
    card: done ? "border-emerald-200/80 bg-emerald-50/50" : bad ? "border-red-200/80 bg-red-50/50" : "border-slate-200 bg-white",
    dot: done ? "bg-emerald-500" : bad ? "bg-red-500" : act ? "bg-sky-500" : "bg-slate-300",
  };
};
/* 【2026-10-29 修「合并节点日期莫名差一天」】
   原来这里是 `arr.filter(Boolean).sort().pop()` —— **字符串字典序**取最大。
   迁移前所有 dueDate 都是同一个形态（洛杉矶零点瞬间）时它恰好等价，但库里现在
   同时存在三种形态（见 design_module._stage_due_day 注释）：
     · 纯日期           2026-12-22
     · 洛杉矶零点瞬间   2026-12-22T08:00:00+00:00（冬令时）/ T07:00:00（夏令时）
     · 历史建项时刻     2026-12-22T00:09:00+00:00（LA 其实是 12-21）
   混排时字典序比的是字符，`2026-12-22` 与 `2026-12-22T00:09:00+00:00` 谁大取决于
   有没有 T、时分秒是不是 0，与真实日历日无关 —— IT 报的「矩阵 12/21、详情 12/22、
   改期窗口 12/21」就是同一个 dueDate 在不同入口被不同形态选出不同天。

   现在改成按**洛杉矶日历日**比较：先用 dateInputValue（usDate 口径）把任意形态归一到
   `YYYY-MM-DD`，再取字典序最大（归一后格式统一，字典序 = 日历序），
   最后回传原始值 —— 保留原始形态给改期弹窗的 dateInputValue 使用。
   形态无法解析时退回字符串前 10 位，保证不会返回 null。 */
const maxD = (arr) => {
  const vals = (arr || []).filter(Boolean);
  if (!vals.length) return null;
  let best = vals[0];
  let bestKey = dateInputValue(best) || String(best).slice(0, 10);
  for (let i = 1; i < vals.length; i += 1) {
    const k = dateInputValue(vals[i]) || String(vals[i]).slice(0, 10);
    if (k > bestKey) { best = vals[i]; bestKey = k; }
  }
  return best;
};
const aggregateStageUnit = ({ key, name, members, consolidation = null, concurrent = false }) => ({
  ...members[0],
  id: key || members[0]?.id,
  key: key || members[0]?.key,
  name: name || members[0]?.name,
  status: stageStatusOfMembers(members),
  merged: members.length > 1,
  concurrent,
  consolidation,
  stages: members,
  dueDate: maxD(members.map((m) => m.dueDate)),
  baselineDue: maxD(members.map((m) => m.baselineDue)),
  actualFinish: maxD(members.map((m) => m.actualFinish)),
  overdueDays: Math.max(0, ...members.map((m) => Number(m.overdueDays || 0))),
  overdueItems: members.reduce((a, m) => a + Number(m.overdueItems || 0), 0),
  completion: Math.round(members.reduce((a, m) => a + (m.completion || 0), 0) / Math.max(1, members.length)),
  primaryOwner: members.find((m) => m.primaryOwner)?.primaryOwner || null,
  order: (() => {
    const os = members.map((m) => m.order).filter((o) => o != null);
    return os.length ? Math.min(...os) : null;
  })(),
  tasks: members.flatMap((m) => m.tasks || []),
});

/* 先按当前模板顺序排阶段，再把结构/T24 固定合并到 Building 图。
   存量项目的库内 order 仍是旧顺序（材料在审查前），所以这里不能直接相信 order；
   stageTemplate 才是用户当前看到的流程顺序。 */
const orderedStageRows = (stages, stageTemplate = []) => {
  const rank = Object.fromEntries(STAGE_ORDER_FALLBACK.map((key, i) => [key, i]));
  (stageTemplate || []).forEach((s, i) => { rank[s.key] = i; });
  return [...(stages || [])].sort((a, b) => {
    const ca = stageConsolidationOf(a.key);
    const cb = stageConsolidationOf(b.key);
    const ka = ca?.key || a.key;
    const kb = cb?.key || b.key;
    const ra = rank[ka] ?? (Number.isFinite(Number(a.order)) ? Number(a.order) + 0.5 : 9999);
    const rb = rank[kb] ?? (Number.isFinite(Number(b.order)) ? Number(b.order) + 0.5 : 9999);
    if (ra !== rb) return ra - rb;
    const ia = ca?.keys.indexOf(a.key) ?? 0;
    const ib = cb?.keys.indexOf(b.key) ?? 0;
    return ia - ib;
  });
};

const consolidateStageRows = (stages, stageTemplate = []) => {
  const ordered = orderedStageRows(stages, stageTemplate);
  const out = [];
  const seen = new Set();
  for (const s of ordered) {
    const cg = stageConsolidationOf(s.key);
    if (!cg) {
      out.push({ ...s, stages: [s], merged: false, concurrent: false, consolidation: null });
      continue;
    }
    if (seen.has(cg.key)) continue;
    seen.add(cg.key);
    const members = ordered.filter((x) => cg.keys.includes(x.key));
    out.push(aggregateStageUnit({ key: cg.key, name: cg.name, members, consolidation: cg }));
  }
  return out;
};

/**
 * 详情阶段条的显示节点。
 *
 * 2026-10-23：结构/T24 永久并入 Building 图，不再受 approvalMode 控制。
 * P+B 同步模式仍在其上叠加原有的五组并发节点。默认参数保证
 * SignedDesignBoard 等复用方无需同步改造也能得到正确的固定合并。
 */
export const groupStages = (stages, mode, stageTemplate = []) => {
  const units = consolidateStageRows(stages, stageTemplate);
  if (mode !== "concurrent") return units;
  const unitOrder = new Map(units.map((u, i) => [u.key, i]));

  const grouped = CONCURRENT_GROUPS.map((g, index) => {
    const memberUnits = units.filter((u) => u.stages.some((m) => g.keys.includes(m.key)));
    if (!memberUnits.length) return null;
    const members = memberUnits.flatMap((u) => u.stages);
    return {
      ...aggregateStageUnit({
        key: g.key,
        name: g.name,
        members,
        concurrent: true,
        consolidation: memberUnits.find((u) => u.consolidation)?.consolidation || null,
      }),
      icon: g.icon,
      /* 用当前模板排好的节点位置取“最靠后的成员”。例如 Building 材料
         被移到 Building 审之后，pb_paperwork 也必须随之排到 pb_review 后。 */
      _sort: Math.max(...memberUnits.map((u) => unitOrder.get(u.key) ?? 9999)),
      _groupOrder: index,
    };
  }).filter(Boolean);
  const used = new Set(CONCURRENT_GROUPS.flatMap((g) => g.keys));
  const standalone = units
    .filter((u) => !u.stages.some((m) => used.has(m.key)))
    .map((u) => ({ ...u, _sort: unitOrder.get(u.key) ?? 9999, _groupOrder: -1 }));
  return [...standalone, ...grouped]
    .sort((a, b) => a._sort - b._sort || a._groupOrder - b._groupOrder)
    .map(({ _sort, _groupOrder, ...u }) => u);
};
export const groupNodes = (nodes, groups) => {
  const nmap = Object.fromEntries((nodes?.stages || []).map((n) => [n.key, n]));
  for (const g of groups.filter((x) => x.merged)) {
    const ns = g.stages.map((m) => nmap[m.key]).filter(Boolean);
    if (ns.length) nmap[g.key] = {
      key: g.key,
      /* dueDate / primaryOwner 也要聚合：LinkedDeliverables 的区块头会显示「截止 X」，
         合并节点若没有这个字段会显示「截止 —」，与节点卡上方的截止日自相矛盾。 */
      dueDate: maxD(ns.map((n) => n.dueDate)),
      primaryOwner: ns.find((n) => n.primaryOwner)?.primaryOwner || null,
      overdueDays: Math.max(0, ...ns.map((n) => n.overdueDays || 0)),
      overdueItems: ns.reduce((a, n) => a + (n.overdueItems || 0), 0),
      /* P+B 区块头不能只显示组内最晚日期：较早的 Planning 逾期时，Building 的未来日期
         会让整组看起来像未逾期。保留聚合字段供原有门禁使用，并额外下发每个成员的日期事实。 */
      concurrent: !!g.concurrent,
      members: g.concurrent ? g.stages.map((m) => {
        const n = nmap[m.key];
        return n ? { key: m.key, name: m.name || n.name || m.key, dueDate: n.dueDate,
          overdueDays: Number(n.overdueDays || 0), overdueItems: Number(n.overdueItems || 0) } : null;
      }).filter(Boolean) : [],
      disciplines: ns.flatMap((n) => n.disciplines || []),
      agencies: ns.flatMap((n) => n.agencies || []),
      payments: ns.flatMap((n) => n.payments || []),
      /* 阶段会话 / 关阶段门禁要用：合并节点对应哪些真实阶段 id。 */
      stageIds: ns.map((n) => ({ id: n.id, key: n.key, done: n.done })),
    };
  }
  return nmap;
};
const d10 = (v) => (v ? fmtD(v) : "—");
/* P+B 汇总只需用短标识区分成员，完整阶段名称已在任务卡中显示。 */
const stageMemberShortLabel = (key) => {
  if (String(key || "").startsWith("planning")) return "P";
  if (key === "structural_t24") return "S/T24";
  if (String(key || "").startsWith("building")) return "B";
  return key || "—";
};
/* 阶段条 / 甘特用的短日期 MM/DD。
   【2026-10-29 时区统一】原来直接截 ISO 字符串（`slice(5,10)`）取「月-日」，取的是
   **UTC** 那一天。历史截止日多是建项时刻写入的（如 2026-10-30T00:09:23Z），洛杉矶看是
   10-29，阶段条却显示 10-30，同一个阶段在阶段卡上是 10/29、在阶段条上是 10-30 自相矛盾。
   改走 fmtMD：带时区的瞬间按洛杉矶换算后再取月/日，纯日期字面量原样取。 */
const md = (v) => (v ? fmtMD(v) : "—");
const fileUrl = (fid) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;
export const inferFileType = (name, req, have) => {
  const ext = (String(name).split(".").pop() || "").toLowerCase();
  const missing = req.filter((r) => !have.includes(r));
  const pool = missing.length ? missing : req;
  return (["dwg", "dxf"].includes(ext) && pool.find((r) => /cad/i.test(r))) || (ext === "pdf" && pool.find((r) => /pdf/i.test(r))) || (["png", "jpg", "jpeg", "webp", "gif"].includes(ext) && pool.find((r) => /照片|photo|图/i.test(r))) || pool[0] || "Deliverable";
};

export const useLegacyDetail = (pid) => {
  const { api } = useApp();
  const [d, setD] = useState(null);
  const [lmeta, setLmeta] = useState(null);
  const load = useCallback(() => api.get(`/design/projects/${pid}`).then(({ data }) => setD(data)).catch((e) => setD({ error: e?.detailText || e.message })), [api, pid]);
  useEffect(() => { load(); api.get("/design/meta").then(({ data }) => setLmeta(data)).catch(() => setLmeta({})); }, [load, api]);
  return { d, lmeta, load };
};

const useNodes = (pid, dep) => {
  const { api } = useApp();
  const [nodes, setNodes] = useState(null);
  const load = useCallback(() => api.get(`/design/v3/projects/${pid}/nodes`).then(({ data }) => setNodes(data)).catch(() => setNodes({ stages: [], items: [], overdue: 0, byOwner: [] })), [api, pid]);
  useEffect(() => { load(); }, [load, dep]);
  return { nodes, reloadNodes: load };
};

// ---------- 节点检查条 ----------
/* 条目类别（2026-10-29 口径反转后只剩两类）：
   阶段 + 结构清单。子任务 / 交付物 / 机构不再单独成条 —— 用户原话
   「直接不要内部那个一起显示了，只要阶段的……我不管内部要做多少事情，只按照阶段时间来」，
   配合「内部暂时不做单独计时」。结构清单保留，因为它是**独立清单项**
   （计算书 / S 系列图 / T24 / CF1R / 工程师盖章各有自己的日期），不是某阶段内部的事。
   KIND_ZH 仍保留全部键：旧数据 / 缓存里可能还有 task 等 kind，缺键会显示成原始英文。 */
const KIND_ZH = { stage: "阶段", task: "子任务", deliverable: "交付物", agency: "机构", agency_unknown: "机构研判", structural: "结构清单" };
export const NodeCheckBar = ({ nodes, nameOf, onJump }) => {
  const [open, setOpen] = useState(false);
  if (!nodes) return null;
  const n = nodes.overdue || 0;
  /* 逾期条目按来源分类计数。2026-10-29 之后实际只会出现「阶段」和「结构清单」两类，
     但这里仍按 kind 动态分组，不做硬编码白名单 —— 万一将来又把某类展开回来，
     汇总行会自动跟上，不会出现「明细里有、汇总里没算」的静默漏计。 */
  const kinds = (nodes.items || []).reduce((acc, it) => {
    acc[it.kind] = (acc[it.kind] || 0) + 1;
    return acc;
  }, {});
  const KIND_ORDER = ["stage", "structural", "task", "deliverable", "agency", "agency_unknown"];
  const kindSummary = KIND_ORDER.filter((k) => kinds[k]).map((k) => `${kinds[k]} ${T(KIND_ZH[k])}`);
  /* 「未轮到」条目数：IT 340 行问「尚未激活或被前置条件锁定的项目如何计数」，
     用户口径是「保留并标未轮到」—— 条目和天数都不动（阶段确实超期了，
     抹掉会掩盖真实滑期），只在读者眼前把「今天能推的」和「只能等上游的」分开。
     数来自后端 overdueLocked（收起态也要显示，所以不让前端自己数明细）。 */
  const lockedN = Number(nodes.overdueLocked || 0);
  return (
    <div className={`rounded-lg border p-2.5 ${n ? "bg-red-50/70 border-red-200" : "bg-white border-slate-200"}`} data-testid="dv3-nodecheck" data-overdue={n} data-overdue-locked={lockedN}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {n ? <AlertTriangle className="h-4 w-4 text-red-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
        {/* 量纲改成「N 个阶段逾期」（2026-10-29）：现在每条就是一个阶段，
            再说「N 项逾期」会被读成 N 件事；说「N 个阶段」才对得上数量含义。 */}
        <b className={n ? "text-red-700" : "text-emerald-700"} data-testid="dv3-nodecheck-summary">{nodes.handedOff ? T("已移交施工 · 节点检查结束") : n ? T("节点检查：{0} 个阶段逾期{1}", [n, lockedN ? T("（其中 {0} 项未轮到）", [lockedN]) : ""]) : T("节点检查：所有阶段节点按期")}</b>
        {n > 0 && (nodes.byOwner || []).map((o) => <button key={o.owner} type="button" data-testid={`dv3-nodecheck-owner-${o.owner}`} onClick={() => setOpen(!open)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] px-2 py-0.5 rounded-full bg-white border border-red-200 text-red-700 font-semibold hover:bg-red-100">{o.name || nameOf?.(o.owner) || o.owner} {o.count}</button>)}
        {n > 0 && <button type="button" data-testid="dv3-nodecheck-toggle" onClick={() => setOpen(!open)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto text-[11px] text-red-700 font-semibold inline-flex items-center gap-0.5">{open ? T("收起") : T("明细")}{open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}</button>}
      </div>
      {/* 口径说明（常驻小字）。这两句话是 IT 反复追问的落点，必须留在页面上：
          ① 「19 项不等于 19 次延误」—— 340 Wilton 那行的原始误解；
          ② 「阶段内部不单独计时」—— 1049 反复问「子任务写 10/28、为什么显示逾期 1 天」
             就是因为旧版把阶段天数复制到了子任务行上，日期却印子任务自己的。 */}
      {n > 0 && (
        <p className="mt-1 text-[10px] leading-relaxed text-red-800/80" data-testid="dv3-nodecheck-breakdown">
          <span data-testid="dv3-nodecheck-breakdown-kinds">{kindSummary.join(" · ")}</span>
          <span className="text-red-700/70"> · {T("按阶段计时：阶段内部要做多少子任务、交多少图、跑多少机构，都不单独计时，只按阶段截止日算逾期。", [])}</span>
        </p>
      )}
      {open && n > 0 && (
        <div className="mt-2 space-y-1" data-testid="dv3-nodecheck-list">
          {KIND_ORDER.filter((k) => kinds[k]).map((k) => (
            <div key={k} className="pt-1" data-testid={`dv3-nodecheck-group-${k}`}>
              <p className="text-[10px] font-bold uppercase tracking-wide text-red-700/80">{T(KIND_ZH[k])} · {kinds[k]}</p>
              {nodes.items.filter((it) => it.kind === k).map((it) => (
                <button key={`${T(it.kind)}-${it.id}`} type="button" data-testid={`dv3-nodecheck-item-${T(it.kind)}-${it.id}`} onClick={() => onJump?.(it.stageKey)} data-due={it.dueDate ? d10(it.dueDate) : ""} data-blocked-by={it.blockedBy || ""} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 w-full text-left text-[11px] flex flex-wrap items-center gap-2 px-2 py-1 rounded-md bg-white/70 hover:bg-white border border-red-100">
                  <span className="px-1.5 rounded bg-red-100 text-red-700 font-semibold">{T(KIND_ZH[it.kind] || it.kind)}</span>
                  <span className="text-slate-800 flex-1 truncate">{T(it.label)}{it.gate ? T(" · 门禁") : ""}</span>
                  <span className="text-slate-400">{T(STAGE_SHORT[it.stageKey] || it.stageKey)}</span>
                  {/* 截止日（2026-10-29 新增）：IT 明确要求「显示触发逾期的子阶段、任务及截止日」。
                      取的是驱动这条天数的日期（阶段 / 清单项自己的 dueDate），
                      与前面的「逾期 N 天」严格同源，不会出现「日期比今天晚却已逾期」的矛盾。 */}
                  {it.dueDate && <span className="text-slate-500 tabular-nums" data-testid={`dv3-nodecheck-due-${T(it.kind)}-${it.id}`}>{T("阶段截止 {0}", [d10(it.dueDate)])}</span>}
                  <span className="text-red-700 font-bold">{T("逾期 {0} 天", [it.days])}</span>
                  {/* 未轮到（2026-10-29 新增）：该阶段被上游未完成阶段锁住，今天推不动。
                      条目不删、天数不改（阶段确实超期，抹掉会掩盖真实滑期），
                      只把「卡在哪一步」写在行内，读者不必点进阶段会话才知道为什么没动静。 */}
                  {it.blockedBy && <span className="px-1.5 rounded bg-slate-100 text-slate-600 border border-slate-200" data-testid={`dv3-nodecheck-blocked-${T(it.kind)}-${it.id}`} title={T("本条所在阶段被上游「{0}」锁住，上游完成前无法推进；逾期天数照常累计，不计入可推进事项。", [it.blockedBy])}>{T("未轮到 · 等{0}", [it.blockedBy])}</span>}
                  <span className="text-slate-500">{it.owner ? nameOf?.(it.owner) || it.owner : it.ownerLabel || T("未指派")}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- 阶段图标步骤条 ----------
const StageStepper = ({ stages, nmap, cardContents, currentStage, sel, onSelect, nameOf }) => {
  const initials = (un) => { const s = nameOf(un) || ""; return s ? s.slice(0, 2).toUpperCase() : ""; };
  /* 【2026-10-23 三态位置化】整条流水线一次算好每个节点的三态（含流程前沿判定）。
     在这里算而不是逐节点算：节点是不是「进行中」取决于**前面收口了没有**，
     单个节点自己看不出来。口径见 meta.js 的 stagePhasesOf 注释。
     currentStage 只剩「选中态 / 深链」用途，不再参与颜色与状态词 ——
     指针会因一次通过 / 补关历史阶段而跳站，跟着它走就会出现「前面没做、后面已成进行中」。 */
  const { phaseOf, nodePhaseOf } = stagePhasesOf(stages.map((s) => ({
    key: s.key,
    members: (s.merged ? s.stages : [s]).map((m) => ({ key: m.key, status: m.status, actualFinish: m.actualFinish })),
  })));
  const phaseOfNode = (s) => nodePhaseOf[s.key] || phaseOf[s.key] || STAGE_PHASE.not_started;
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 overflow-x-auto [scrollbar-width:thin]" data-testid="dv3-stepper">
      <div className="flex items-start min-w-max">
        {stages.map((s, i) => {
          const n = nmap[s.key] || {};
          const Icon = STAGE_ICON[s.key] || CONCURRENT_GROUPS.find((g) => g.key === s.key)?.icon || Layers;
          /* 三态（2026-10-23）：圆环 / 文字 / 连接线全部改按显示态判定，不再单独写
             `status === "Completed"` —— 否则 Approved / Not Applicable 明明显示「已完成」，
             圆环却还是灰的，同一节点自相矛盾。done 保留给「已收口」的业务判断（角标等）。
             圆环颜色只有四档（已完成 / 进行中 / 逾期阻塞 / 未开始），
             原来那档「当前阶段 indigo」已删除：它跟着 currentStage 指针走，
             指针跳站时会让人以为流程已经走到后面。 */
          const phase = phaseOfNode(s);
          const done = phase === STAGE_PHASE.completed;
          const overdue = (n.overdueDays || 0) > 0;
          const ring = done ? "bg-emerald-500 border-emerald-500 text-white" : s.status === "Blocked" || overdue ? "bg-red-50 border-red-500 text-red-600" : phase === STAGE_PHASE.in_progress ? "bg-sky-500 border-sky-500 text-white" : "bg-white border-slate-200 text-slate-400";
          const cards = cardContents[s.key];
          const cardCount = cards.shownTasks.length + cards.shownDiscs.length + cards.restAgs.length;
          const cardSummary = T("阶段卡片 {0} 张：任务 {1}、交付物 {2}、独立机构 {3}（不含专项面板及交图邮件）", [cardCount, cards.shownTasks.length, cards.shownDiscs.length, cards.restAgs.length]);
          const overdueBadge = n.overdueItems > 0 && !done ? T("红色角标：{0} 项逾期", [n.overdueItems]) : "";
          const stepTitle = `${T("{0} · {1} · 截止 {2}{3}", [T(s.name), T(STAGE_PHASE_LABEL[phase]), d10(s.dueDate), overdue ? T(" · 逾期 {0} 天", [n.overdueDays]) : ""])} · ${cardSummary}${overdueBadge ? ` · ${overdueBadge}` : ""}`;
          return (
            <React.Fragment key={s.id}>
              {i > 0 && <div className={`h-0.5 w-5 mt-5 shrink-0 ${done || phaseOfNode(stages[i - 1]) === STAGE_PHASE.completed ? "bg-emerald-400" : "bg-slate-200"}`} />}
              {/* 三态（2026-10-23）：节点状态词只出现「未开始 / 进行中 / 已完成」，
                  逾期天数与「阻塞 / 等市府」这类附加信息跟在后面，不再把细化态当状态显示。
                  data-status 仍是原始 12 态（既有 E2E 与调试依赖），data-phase 是显示三态。 */}
              <button type="button" data-testid={`dv3-step-${s.key}`} data-status={s.status}
                data-phase={phase} data-overdue={overdue ? n.overdueDays : 0} onClick={() => onSelect(s.key)}
                title={stepTitle} aria-label={stepTitle}
                className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`group flex flex-col items-center ${s.merged ? "w-[112px]" : "w-[74px]"} shrink-0 rounded-lg py-1 transition-colors ${sel === s.key ? "bg-slate-100" : "hover:bg-slate-50"}`)} data-merged={s.merged ? s.stages.length : 0}>
                <span className={`relative h-10 w-10 rounded-full border-2 flex items-center justify-center transition-transform group-hover:scale-105 ${ring} ${overdue ? "animate-pulse" : ""} ${s.merged ? "ring-2 ring-teal-200 ring-offset-1" : ""}`}>
                  <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
                  {cardCount > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 px-0.5 rounded-full bg-violet-600 text-white text-[9px] font-bold flex items-center justify-center" data-testid={`dv3-step-linked-${s.key}`}>{cardCount}</span>}
                  {n.overdueItems > 0 && !done && <span className="absolute -bottom-1 -right-1 h-4 min-w-4 px-0.5 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center" data-testid={`dv3-step-overdue-${s.key}`}>{n.overdueItems}</span>}
                </span>
                <span className={`mt-1 text-[10px] leading-tight text-center font-semibold ${done ? "text-emerald-700" : phase === STAGE_PHASE.in_progress ? "text-sky-700" : overdue ? "text-red-700" : "text-slate-600"}`}>{T(STAGE_SHORT[s.key] || s.name)}</span>
                {s.merged && s.consolidation && !s.concurrent ? (
                  <span className="mt-0.5 w-full text-center text-[9px] leading-tight text-slate-400" data-testid={`dv3-step-consolidated-${s.key}`}>
                    {T(s.consolidation.note)}
                  </span>
                ) : s.merged ? (
                  <span className="mt-0.5 w-full px-0.5 space-y-px" data-testid={`dv3-step-pb-${s.key}`}>
                    {s.stages.map((m) => {
                      const mo = (nmap[m.key] || {}).overdueDays || 0;
                      /* 成员行也走整条流水线算好的 phaseOf：同一节点里 P 和 B 各自
                         只要「轮到自己」才进行中，不再看 currentStage 指针
                         （指针会因一次通过 / 补关历史阶段跳到还没走到的格子）。 */
                      const mp = phaseOf[m.key] || STAGE_PHASE.not_started;
                      const mdone = mp === STAGE_PHASE.completed;
                      const c = mdone ? "text-emerald-700" : mo > 0 || m.status === "Blocked" ? "text-red-600 font-bold" : mp === STAGE_PHASE.in_progress ? "text-sky-700 font-semibold" : "text-slate-400";
                      return (
                        <span key={m.key} className={`flex items-center justify-between gap-1 text-[9px] leading-tight ${c}`} data-testid={`dv3-step-pb-${s.key}-${m.key.startsWith("planning") ? "p" : "b"}`} data-status={m.status} data-phase={mp} title={`${T(STAGE_SHORT[m.key] || m.name)} · ${T(STAGE_PHASE_LABEL[mp])}`}>
                          <b className="shrink-0">{m.key.startsWith("planning") ? "P" : "B"}</b>
                          {/* 成员行同样只给三态词（2026-10-23）：原来的「阻塞 / 待开始 / 进行中」
                              混着两套口径。逾期天数保留 —— 那是提醒，不是状态。 */}
                          <span className="truncate">{mdone ? `✓ ${md(m.actualFinish)}` : mo > 0 ? T("逾期 {0} 天", [mo]) : mp === STAGE_PHASE.not_started ? T(STAGE_PHASE_LABEL[mp]) : T("{0} · 止 {1}", [T(STAGE_PHASE_LABEL[mp]), md(m.dueDate)])}</span>
                        </span>
                      );
                    })}
                  </span>
                ) : (
                  <span className={`text-[9px] ${overdue ? "text-red-600 font-bold" : "text-slate-400"}`}>{done ? `✓ ${md(s.actualFinish)}` : T("止 {0}", [md(s.dueDate)])}</span>
                )}
                {s.primaryOwner ? <span className="mt-0.5 h-4 w-4 rounded-full bg-slate-800 text-white text-[8px] font-bold flex items-center justify-center" title={nameOf(s.primaryOwner)}>{initials(s.primaryOwner)}</span> : <span className="mt-0.5 h-4 w-4 rounded-full border border-dashed border-slate-300" title={T("未指派")} />}
              </button>
            </React.Fragment>
          );
        })}
      </div>
      {/* 图例：原来是一长串文字（"绿 已完成 · 蓝 当前 · …"），读起来像说明书。
          改成真图例（色点 + 短标签），角标含义收进右侧一行小字。
          【2026-10-23 三态化】「当前阶段」那一档去掉了：圆环颜色现在只表达三态，
          不再有独立的「当前」色（原来 indigo 那档就是被误读成「跳到最后」的来源）。 */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 pt-2 text-[10px] text-slate-500" data-testid="dv3-stepper-legend">
        {[["bg-emerald-500", "已完成"], ["bg-sky-500", "进行中"], ["bg-red-500", "逾期 / 阻塞"], ["bg-slate-300", "未开始"]].map(([c, l]) => (
          <span key={l} className="inline-flex items-center gap-1"><i className={`h-1.5 w-1.5 rounded-full ${c}`} />{T(l)}</span>
        ))}
        <span className="ml-auto text-slate-400">{T("角标：紫 = 任务 / 交付物 / 独立机构卡片 · 红 = 逾期项 · 点节点查看")}</span>
      </div>
    </div>
  );
};

// ---------- 阶段内挂载：交付物 / 机构 / 付款 ----------
/* 产物已完成、但本阶段挂的机构全都没开始 → 需要一句说明（2026-10-29，见调用处注释）。
 *
 * 判定刻意收得很窄，只命中最典型的脱节形态：
 *   · 本阶段**至少有一项产物已完成**（done / approved / exempt）—— 说明设计侧交付过了；
 *   · 本阶段挂载的机构**每一项都是 Not Started**（无 dueDate 进展、无 statusAt）。
 * 任一机构有进展（Requested / Submitted / Approved…）就不再提示：那时业务上已经
 * 递出去了，状态不一致的说法不成立，留着只会变成常驻噪音。
 * 机构挂载用 agByDisc（产物 → 机构的反向表），与卡片上显示的是同一份数据。
 */
const AGENCY_UNTOUCHED = (a) => (a?.status || "Not Started") === "Not Started" && !a?.statusAt;
const DISC_DELIVERED = new Set(["done", "approved", "exempt"]);
const _agenciesStaleNote = (node, agByDisc) => {
  const discList = node?.disciplines || [];
  if (!discList.some((d) => DISC_DELIVERED.has(d?.status))) return false;
  const linked = discList.flatMap((d) => agByDisc[d.key] || []);
  if (!linked.length) return false;
  return linked.every(AGENCY_UNTOUCHED);
};

// 列数自适应钩子统一放在 meta.js（useGridCols）：详情抽屉的「交付物」页签也用同一个，
// 避免两处各写一份、改一处漏一处（2026-10-18 用户反馈「详情又一行一个了」）。

const LinkedDeliverables = ({ pid, node, p, meta, api, onChanged, agencies = [], agencyDocs = {}, designers = [], stageTasks = [], shownDiscs, carriedDiscs, agToDisc, onOpenLegacyFiles, agencyResp = {} }) => {
  const inp = useRef(null);
  const upKey = useRef(null);
  const [busy, setBusy] = useState(null);
  const [dropKey, setDropKey] = useState(null);
  /* 产物卡信息量小（名称 + 状态 + 文件数 + 2 个按钮）→ 单卡 260px 起，最多 4 列 */
  const [cols, bindGrid] = useGridCols(260, 4);
  if (!node?.disciplines?.length) return null;
  const discs = Object.fromEntries((p?.disciplines || []).map((d) => [d.key, d]));
  /* 配对产物的渲染口径（2026-10-22 用户最终确认）。
     用户明确：「Site Plan + Floor Plan (CAD/PDF) 这种两个…保留我发的这个就行…
     详情里的显示不是已经删除了另外一个了吗为什么又写回列表这边来了」——
     一份交付只出现一次，保留任务卡（它带负责人 / 上传 / 文件与版本 / 审核通过 / 退回重做），
     配对的产物卡收起。

     历史（避免再翻车）：2026-10-21 曾把产物卡整张收起、只留一行灰字，当时的问题是
     审核动作也一起消失；现在审核动作已搬到任务卡上（见下方 dv3-ltask-pairdisc-review-*），
     所以收起不再有副作用。2026-10-21 晚些又改成「两张卡都保留」，那是错的 ——
     同一份交付会上下各出现一次，用户看到的就是重复。 */
  const set = async (key, status) => {
    setBusy(key);
    try { const { data } = await api.patch(`/design/v3/projects/${pid}/disciplines/${key}`, { status }); toast.success(T("已更新为「{0}」", [discStatusLabel(meta, status, discs[key]?.discKind)])); (data.automation || []).forEach((a) => toast.message(T("自动化 {0}", [a.rule.toUpperCase()]), { description: a.text })); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };
  const upload = async (key, f, mark = true) => {
    if (!f) return;
    setBusy(key);
    try {
      const up = await uploadChunked(api, f, { shrink: false });
      const fd = new FormData();
      appendUploadRef(fd, up, f);
      fd.append("markSubmitted", mark ? "1" : "0");
      fd.append("note", "");
      const { data } = await api.post(`/design/v3/projects/${pid}/disciplines/${key}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      /* 越级上传（2026-10-23 用户需求「阶段限制时后面的阶段依然可以让先将资料产物等先上传上去...
         但是不会改变阶段显示的状态」）：后端放行但只存档，勾了「标记为已提交」也不会生效。
         提示按 deferred 分支，避免用户以为这一版已经送审了。 */
      toast.success(data.deferred
        ? T("已存档 v{0}（流程还没走到本阶段，状态未变）", [data.file.version])
        : (mark ? T("已上传 v{0} 并标记为已提交", [data.file.version]) : T("已上传 v{0}（状态未变）", [data.file.version])));
      onChanged();
    }
    catch (e) { toast.error(uploadErrText(e, true)); }
    setBusy(null); upKey.current = null;
  };
  const dropOn = async (key, fileList) => {
    const arr = Array.from(fileList || []).filter((f) => f.size <= UPLOAD_MAX);
    if (!arr.length) return toast.error(T("没有可上传的文件（≤200MB）"));
    for (const f of arr) await upload(key, f, false); // 拖拽直传：只留档，不自动标「已提交」
  };
  const dragProps = (key) => (meta.canEdit ? {
    onDragOver: (e) => { if (!e.dataTransfer?.types?.includes("Files")) return; e.preventDefault(); e.stopPropagation(); if (dropKey !== key) setDropKey(key); },
    onDragLeave: () => setDropKey(null),
    onDrop: (e) => { e.preventDefault(); e.stopPropagation(); setDropKey(null); dropOn(key, e.dataTransfer.files); },
  } : {});
  const agByDisc = {};
  (agencies || []).forEach((a) => { const k = agToDisc[a.type]; if (k) (agByDisc[k] = agByDisc[k] || []).push(a); });
  const doneN = node.disciplines.filter((d) => d.done).length;
  const pctN = node.disciplines.length ? Math.round((doneN / node.disciplines.length) * 100) : 0;
  return (
    <div className="space-y-2" data-testid={`dv3-stage-discs-${node.key}`}>
      {/* 区块头先给一眼能扫的结论；P+B 节点逐成员显示日期，避免最晚日期遮住较早成员的逾期。 */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-0.5">
        <ListChecks className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        <span className="text-[11px] font-bold uppercase tracking-wide text-slate-600">{T("交付物")}</span>
        <span className="text-[11px] font-semibold text-slate-900 tabular-nums" data-testid={`dv3-stage-discs-count-${node.key}`}>{doneN}/{node.disciplines.length}</span>
        <span className="h-1 w-20 rounded-full bg-slate-200 overflow-hidden shrink-0"><span className="block h-full rounded-full bg-emerald-500" style={{ width: `${pctN}%` }} /></span>
        {node.concurrent && node.members?.length ? (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px]" data-testid={`dv3-stage-discs-member-due-${node.key}`}>
            {node.members.map((member) => (
              <span key={member.key} className={`inline-flex items-center gap-1 whitespace-nowrap ${member.overdueDays > 0 ? "font-semibold text-red-600" : "text-slate-500"}`}
                data-testid={`dv3-stage-discs-member-due-${node.key}-${member.key}`} data-overdue={member.overdueDays > 0 ? "1" : "0"}>
                <span className="font-bold">{stageMemberShortLabel(member.key)}</span>
                <span>{T("截止 {0}", [d10(member.dueDate)])}</span>
                {member.overdueDays > 0 && <span>{T(" · 逾期 {0} 天", [member.overdueDays])}</span>}
              </span>
            ))}
          </div>
        ) : <span className="text-[10px] text-slate-400 truncate">{T("截止 {0}", [d10(node.dueDate)])}</span>}
        {meta.canEdit && <span className="ml-auto hidden xl:inline text-[10px] text-slate-400 shrink-0">{T("拖图纸到卡片即上传（不改状态）")}</span>}
      </div>
      {/* 产物 ↔ 机构状态不同步的说明（2026-10-29 新增，回应用户 1049 Steinway 那条
         「上层 Fire Flow Test / CND / Sanitation 为 Completed、Grading 为 Exempt，
         下层 Agency 均 Not Started，状态差异及前置锁定是否参与逾期计算，仍待 IT 确认」）。

         这不是 bug，而是两条独立的状态轨道：
           · 产物（disciplines）是**设计交付**：图纸/计算书，设计部自己交、内部审核；
           · 机构（agencies）是**对外报批**：City / Fire / 学区等外部单位办理，
             只有真的递出去、对方受理了才会离开 Not Started。
         一个产物可以「图纸已完成」而它对应的机构仍「未开始」—— 图纸做完但还没递报批，
         业务上完全正常。所以这里不改任何状态、不做自动联动（自动把机构置为进行中
         会伪造外部事实），只在两者明显脱节时给一句说明，避免被误读成数据不一致。

         判定只取「本阶段内已完成产物 ≥1 且本阶段挂的机构全部未开始」这一种最典型的
         形态；机构一旦有任何进展就不再提示，防止常驻噪音。 */}
      {_agenciesStaleNote(node, agByDisc) && (
        <p className="px-0.5 text-[10px] leading-relaxed text-slate-500" data-testid={`dv3-stage-discs-agency-note-${node.key}`}>
          <Info className="h-3 w-3 inline mr-0.5 -mt-0.5 text-slate-400" aria-hidden="true" />
          {T("本阶段产物已完成、但挂载的机构对接项均为「未开始」：产物是设计交付（图纸/计算书），机构是外部报批（City/Fire 等），两者各自独立推进，图纸做完并不代表已递报批。")}
        </p>
      )}
      <input ref={inp} type="file" className="hidden" accept={DESIGN_UPLOAD_ACCEPT} data-testid={`dv3-stage-disc-upload-input-${node.key}`} onChange={(e) => { upload(upKey.current, e.target.files?.[0]); e.target.value = ""; }} />
      {/* 列数由**容器宽度**决定（不是视口断点）：抽屉可拖拽调宽，固定 4 列在窄抽屉会挤爆。
          默认 stretch（不写 items-start）→ 同一行的卡片等高，机构条统一贴在卡片底部。 */}
      <div ref={bindGrid} className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {shownDiscs.map((d) => {
        const live = discs[d.key] || d;
        const st = live.status;
        const c = DS_COLOR[st] || { c: "#475569", bg: "#f1f5f9" };
        const tone = DISC_TONE(st);
        const over = dropKey === d.key;
        const fileN = (live.files || []).filter((f) => !f.deleted && !f.voided).length;
        const mineAgs = agByDisc[d.key] || [];
        return (
          <div key={d.key}
            title={T("产物：状态 / 文件数 / 机构见卡片，拖入图纸可直接上传") + (meta.canEdit ? T("（不改状态）") : "")} {...dragProps(d.key)}
            className={`flex flex-col rounded-lg border select-none transition-colors ${tone.card} ${over ? "border-violet-400 ring-2 ring-violet-200/70" : "hover:border-slate-300"}`} data-testid={`dv3-stage-disc-${node.key}-${d.key}`} data-drop={over ? "1" : "0"}>
            {/* 卡片分三段：① 状态 + 名称（名称独占一行，不再被右侧图标挤成 "Fire Sprinkler / F…"）
                ② 状态药丸 + 文件数 + 操作（操作靠右、低对比，hover 才变紫）
                ③ 机构条（白底 + 顶部 hairline，读作卡片页脚，不再套一层带边框的小盒子） */}
            <div className="flex-1 p-3">
              <div className="flex items-start gap-1.5 min-w-0">
                {tone.done ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" data-testid={`dv3-stage-disc-done-${node.key}-${d.key}`} /> : <span className={`h-2 w-2 rounded-full shrink-0 mt-1.5 ${tone.dot}`} />}
                <span className="text-[13px] leading-5 font-semibold text-slate-800 truncate" title={T(d.name)}>{over ? T("松开上传 → {0}", [T(d.name)]) : T(d.name)}</span>
              </div>
              <div className="mt-1.5 flex items-center gap-1.5">
                {/* 产物状态下拉已移除（2026-10-21 用户需求）：
                    「这个选项不需要了……都是必传才能进行下一个步骤」。
                    状态改为由上传来驱动（上传 → 已提交；审核通过 → 完成；
                    无需审核的产物在提交时直接就绪），不再允许人在阶段卡里随手选状态。
                    保留只读药丸：进度与颜色仍是扫读阶段的唯一依据，只是不再可点。
                    需要改状态的唯一合法入口是「提交」业务流程（含审核 / 退回），
                    它同时负责建复核单、通知审核人、触发自动推进，换掉会漏掉这些副作用。 */}
                {busy === d.key ? <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" /> : (
                  <span className="h-[22px] inline-flex items-center text-[10px] rounded-md px-1.5 font-semibold" style={{ color: c.c, background: c.bg }} data-testid={`dv3-stage-disc-status-${node.key}-${d.key}`} data-status={st}>{discStatusLabel(meta, st, live.discKind || d.discKind)}</span>
                )}
                <span className="text-[10px] text-slate-400 tabular-nums truncate" data-testid={`dv3-stage-disc-files-${node.key}-${d.key}`} title={T("{0} 份文件", [fileN])}>{T("{0} 份文件", [fileN])}</span>
                <div className="ml-auto flex items-center gap-0.5 shrink-0">
                  {meta.canEdit && <button type="button" data-testid={`dv3-stage-disc-upload-${node.key}-${d.key}`} title={T("上传图纸（自动标记已提交）")} onClick={() => { upKey.current = d.key; inp.current?.click(); }} className="h-6 w-6 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60"><Paperclip className="h-3.5 w-3.5" /></button>}
                </div>
              </div>
            </div>
            {mineAgs.length > 0 && (
              /* 页脚用半透明白：在白卡上几乎看不出、在彩色卡（已提交/已批准/整改）上又能压住底色，
                 比纯白更"长在卡里"，也不会把一张卡切成两块。 */
              <div className="mt-auto rounded-b-lg border-t border-slate-200/60 bg-white/70 px-3 py-2 space-y-1.5" data-testid={`dv3-stage-disc-ags-${node.key}-${d.key}`}>
                {mineAgs.map((a) => <AgencyRow key={a.id} a={a} meta={meta} api={api} designers={designers} onChanged={onChanged} compact docs={(agencyDocs || {})[a.type] || []} resp={agencyResp} />)}
              </div>
            )}
          </div>
        );
      })}
      </div>
      {/* 被配对任务卡承载的产物：不再单独成卡，但「它交了什么、审到哪」仍要能一眼看到
          （2026-10-22 恢复）。产物卡收起后，产物侧的图纸版本与送审记录是**另一份**数据
          （设计师可能在产物侧传过图、走过审核），这里把它们收成一行摘要并保留
          「产物附件 / 审核」入口，不因收起卡片而失联；审核动作在任务卡上（见 dv3-ltask-pairdisc-review-）。 */}
      {carriedDiscs.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-dashed border-slate-200 bg-slate-50/60 px-2 py-1.5"
          data-testid={`dv3-stage-discs-carried-${node.key}`} data-count={carriedDiscs.length}>
          <span className="text-[10px] font-semibold text-slate-500 shrink-0">{T("已在上面任务卡内办理")}</span>
          {carriedDiscs.map((d) => {
            const live = discs[d.key] || d;
            const c = DS_COLOR[live.status] || { c: "#475569", bg: "#f1f5f9" };
            const fileN = (live.files || []).filter((f) => !f.deleted && !f.voided).length;
            const rev = (p?.reviews || []).find((x) => x.disciplineKey === d.key);
            const task = (stageTasks || []).find((t) => t.title === DISC_TO_TASK[d.key] && !t.isDeleted) || null;
            return (
              <span key={d.key} className="inline-flex items-center gap-1 min-w-0" data-testid={`dv3-stage-disc-carried-${node.key}-${d.key}`} data-status={live.status}>
                {live.done
                  ? <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-600" />
                  : <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${DISC_TONE(live.status).dot}`} />}
                <span className="text-[10px] font-semibold text-slate-600 truncate max-w-[9rem]" title={T(d.name)}>{T(d.name)}</span>
                <span className="text-[10px] rounded px-1 font-semibold shrink-0" style={{ color: c.c, background: c.bg }}>{discStatusLabel(meta, live.status, live.discKind || d.discKind)}</span>
                {(fileN > 0 || rev) && task && (
                  <button type="button" data-testid={`dv3-stage-disc-carried-open-${node.key}-${d.key}`}
                    title={rev?.status === "pending" ? T("产物侧有送审记录：待 {0} 复核", [rev.reviewer || T("设计部经理")]) : T("查看该产物在产物侧上传的图纸与审核记录（{0} 份）", [fileN])}
                    onClick={() => onOpenLegacyFiles?.(task)}
                    className="h-5 inline-flex items-center gap-0.5 rounded px-1 text-[10px] font-semibold text-slate-400 hover:bg-white hover:text-violet-700">
                    <FolderOpen className="h-3 w-3" />{fileN || ""}{rev?.status === "pending" ? <Hourglass className="h-3 w-3 text-amber-500" /> : null}
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* 2026-10-22 导出给阶段会话右侧复用（用户要求「和现在的详情中的阶段对齐」）。
   详情阶段卡有「机构对接」而会话右侧原先完全没有；与其在抽屉里另写一份机构行，
   不如复用同一个组件 —— 状态药丸 / 附件归类 / MEP 三专业缺口这些口径本来就该
   只有一份实现，否则改一处漏一处。 */
export const AgencyRow = ({ a, meta, api, designers, onChanged, compact, docs = [], resp = {} }) => {
  const inp = useRef(null);
  const ctx = useContextMenu("dv3-agency-file-ctx");
  const [busy, setBusy] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [lb, setLb] = useState(null);
  const [pickFor, setPickFor] = useState(null); // 正在给哪个附件补挂清单项（文件 aid），null = 未打开
  const done = AG_DONE.includes(a.status) || a.applicability === "Exempt/Not Applicable";
  const files = a.files || [];
  const sortedFiles = [...files].sort((x, y) => (y.version || 0) - (x.version || 0));
  // 作废的附件不算「已收」—— 否则作废后必需文件清单还显示齐全，等于自欺。
  const have = new Set(files.filter((f) => !f.voided).map((f) => f.docType).filter(Boolean));
  const liveFiles = files.filter((f) => !f.voided);
  const missing = docs.filter((x) => !have.has(x));
  const patch = async (body, okMsg) => {
    setBusy(true);
    try { await api.patch(`/design/agencies/${a.id}`, body); toast.success(okMsg || T("已更新")); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  /* 【2026-10-21 用户要求】上传按钮不再先弹「这份文件对应清单项」二级菜单——
     点一下就直接调起系统文件选择框，传完再说归类。原来那一步是强制前置：
     赶时间只想先把回执/批文存进来的人，会被一个必选题卡住，反而挡住主流程。

     归类能力没有丢，改到文件行上的「归类」按钮按需补挂（见 setDocType 与下方文件行）。

     2026-10-21 第二轮用户反馈「那对应的那个下拉按钮其实也可以不需要是吗」：
     上传后自动展开归类面板这一步也去掉 —— 上传就只是上传，不再有任何后续弹层。
     归类彻底变成「想归的时候自己点」，不做任何自动打扰。
     代价（已知且接受）：新上传的文件在有人归类前不计入「必需文件 已收 n/N」，
     机构卡的缺口数字会暂时偏大；文件行会显示琥珀色「未归类」提示，Mouse 悬停另有说明。
     口径不变 —— 仍按 docType 统计，所以只要有人点了归类，数字立刻回到正确值。

     dt 参数只为「上传新版本」这条右键入口保留：那份文件原本已归好类，
     新版本继承同一清单项才不会让清单回退成缺件，属于用户明确的意图，不需要再问一次。 */
  const upload = async (f, dt = "") => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f); fd.append("note", ""); fd.append("docType", dt);
    try {
      const { data } = await api.post(`/design/agencies/${a.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // 提示里不再出现「→ 清单项」：普通上传必然是未归类，多说一句反而让人以为漏选了什么。
      toast.success(T("已上传 {0}（v{1}）", [data.file.name, data.file.version]));
      setShowFiles(true);
      // 显式关闭归类面板：上传只做上传，不自动弹任何东西。
      // 显式 set 而不是不写，是为了让「刚在归类某个旧文件时又传了新文件」这种情况下
      // 那个旧面板也一并收起，避免残留面板看起来像在给新文件归类。
      setPickFor(null);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  /* 事后补挂 / 改挂清单项。失败时保留弹层，让用户能改选另一个清单项，不用重新走一遍上传。 */
  const setDocType = async (faid, dt) => {
    setBusy(true);
    try {
      await api.patch(`/design/agencies/${a.id}/files/${faid}/doctype`, { docType: dt });
      toast.success(dt ? T("已归类为 {0}", [dt]) : T("已取消归类"));
      setPickFor(null);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  /* 「上传新版本」继承原文件清单项：用一个 ref 把待继承的 docType 带到隐藏 input 的 change 里。
     不用 state 是因为 change 事件里读到的是上一次渲染的闭包值，容易读到空串而丢掉归类。 */
  const pendingDt = useRef("");
  const startUpload = (dt = "") => { pendingDt.current = dt || ""; inp.current?.click(); };
  // 机构附件（回执 / 批文）同样是报审凭证：页面**不提供删除/作废入口**（2026-10-18 用户定调）。
  // 传错了就再传一版，版本号递增，历史永久留档可下载。
  const menuFor = (f, i) => () => [
    { key: "preview", icon: FileCheck2, label: T("预览"), onClick: () => setLb(i) },
    // 下载走 blob 直落盘：后端 download 是 inline 响应，跨域下 <a download> 失效，
    // window.open 只会多出一个空白标签页（2026-10-20 用户反馈）。
    { key: "download", icon: FileDown, label: T("下载"), onClick: () => downloadFile(f.fileId, f.name).catch((e) => toast.error(e?.message || T("下载失败"))) },
    // 补挂 / 改挂清单项（2026-10-21）：上传改成「先传后归类」后，这里是把文件对上清单项的入口。
    // 作废件不给归类入口 —— 历史凭证的口径不能被事后改写。
    meta.canEdit && !f.voided && docs.length && { key: "doctype", icon: ListChecks, label: f.docType ? T("改归类（当前：{0}）", [f.docType]) : T("归类到清单项"), onClick: () => setPickFor(f.aid) },
    meta.canEdit && !f.voided && { key: "new-version", icon: UploadCloud, label: f.docType ? T("上传新版本（{0}）", [f.docType]) : T("上传新版本"), onClick: () => startUpload(f.docType || "") },
  ];
  return (
    <div onDoubleClick={(e) => { if (e.target.closest("select,button,label,input,a")) return; setShowFiles((v) => !v); }} title={T("双击展开 / 收起附件")}
      className={compact
        ? `rounded-md px-2 py-1.5 ${done ? "bg-emerald-50/70" : "bg-slate-50/80"}`
        : `rounded-lg border p-2.5 ${done ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
      data-testid={`dv3-agency-row-${a.id}`} data-done={done ? "1" : "0"}>
      <div className="flex items-center gap-1.5">
        {done ? <CheckCircle2 className={`${compact ? "h-3.5 w-3.5" : "h-4 w-4"} text-emerald-600 shrink-0`} data-testid={`dv3-agency-done-${a.id}`} /> : <Landmark className={`${compact ? "h-3.5 w-3.5" : "h-4 w-4"} text-slate-400 shrink-0`} />}
        <span className={`${compact ? "text-[11px]" : "text-xs"} font-semibold text-slate-800 flex-1 min-w-0 truncate`} title={a.type}>{a.type}</span>
        {!compact && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${a.applicability === "Required" ? "bg-red-100 text-red-700" : a.applicability === "Exempt/Not Applicable" ? "bg-slate-100 text-slate-500" : a.applicability === "Potentially Required" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400"}`}>{a.applicability || "Unknown"}</span>}
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" /> : meta.canEdit ? (
          <select data-testid={`dv3-agency-status-${a.id}`} value={a.status || "Not Started"} onChange={(e) => patch({ status: e.target.value }, `${a.type} → ${T(AG_ZH[e.target.value])}`)} className={`${compact ? "h-[18px] rounded" : "rounded-full"} text-[10px] px-1.5 font-semibold border-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60 ${STATUS_BADGE(a.status)}`}>
            {AGENCY_STATUSES.map((s) => <option key={s} value={s}>{T(AG_ZH[s])}</option>)}
          </select>
        ) : <span className={`${compact ? "rounded" : "rounded-full"} text-[10px] px-1.5 py-0.5 font-semibold ${STATUS_BADGE(a.status)}`}>{AG_ZH[a.status] || a.status || T("未开始")}</span>}
        {/* 【2026-10-22 用户反馈】「机构的负责人的下拉选项还没去掉..都说了是全流程责任那里
            全局配置任务身份了」。

            修改原因：机构「谁去办 / 谁推进」早已在「产物与流程设置 → 全流程责任」按身份
            全局配好（AgencyRespCfg：handleRoles 办理 / advanceRoles 推进），建项目时
            design_preflow.assign_owners 还会按 handleRoles 自动把 owner 预填进机构文档
            （见 design_preflow.py 的「机构办理人」段）。这里再放一个自由选人的下拉，
            等于给了第二个互相矛盾的口径 —— 页面上随手改一个人，全局配置看不出任何变化，
            下个项目又按配置分给另一个人。
            最终效果：这里只读显示「谁在办」（由全局配置自动落到具体账号），不再提供改人入口。
            要换人就去全流程责任改身份配置 —— 一处生效、全部项目一致。
            关键取舍：保留只读展示而不是整行删掉 —— 用户仍需知道这件事现在归谁，
            没有负责人时明确显示「未指派」，避免看起来像已经有人在做。 */}
        {!compact && (() => {
          /* 【2026-10-22 用户需求】「下拉去掉...读配置的负责人身份回显是谁就行了啊」。
             显示的是「全流程责任」里该机构配置的**办理身份**解析到本项目的具体人
             （后端 agencyResp[type].handleNames 已算好），不再是自由选人的下拉。
             身份解析不出来（项目还没分岗）时，退回显示机构文档上已有的 owner 账号，
             两者都没有才显示「未指派」—— 绝不臆造一个负责人。 */
          const names = (resp[a.type] || {}).handleNames || [];
          const fallback = a.owner ? (designers.find((u) => u.username === a.owner)?.name || a.owner) : "";
          const shown = names.length ? names.join("、") : fallback;
          return <span className={`text-[10px] max-w-[110px] truncate ${shown ? "text-slate-500" : "text-slate-300"}`}
            title={T("办理人由「全流程责任」按身份全局配置（{0}）· 如需换人请改那边", [a.type])}
            data-testid={`dv3-agency-owner-${a.id}`}>{shown || T("未指派")}</span>;
        })()}
        {/* 上传按钮＝一步到位：点它直接调起系统文件选择框（2026-10-21 用户要求），
            不再先弹清单项菜单。归类改到文件行上按需处理，见下方 filelist 里的归类弹层。 */}
        {!compact && meta.canEdit && <span className="relative"><input ref={inp} type="file" className="hidden" accept={DESIGN_UPLOAD_ACCEPT} data-testid={`dv3-agency-upload-input-${a.id}`} onChange={(e) => { upload(e.target.files?.[0], pendingDt.current); pendingDt.current = ""; e.target.value = ""; }} /><button type="button" data-testid={`dv3-agency-upload-${a.id}`} title={T("上传附件（回执 / 批文 / 报告）")} onClick={() => startUpload("")} className="text-slate-400 hover:text-violet-700"><UploadCloud className="h-4 w-4" /></button></span>}
        {!compact && <button type="button" data-testid={`dv3-agency-files-${a.id}`} onClick={() => setShowFiles(!showFiles)} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 relative ${liveFiles.length ? "text-violet-700" : "text-slate-300"}`} title={T("附件（有效 {0} / 共 {1}）", [liveFiles.length, files.length])}><Paperclip className="h-4 w-4" />{liveFiles.length > 0 && <span className="absolute -top-1.5 -right-1.5 bg-violet-600 text-white rounded-full text-[8px] w-3.5 h-3.5 flex items-center justify-center">{liveFiles.length}</span>}</button>}
      </div>
      {compact ? (
        <div className="mt-1 flex items-center gap-1.5">
          {docs.length > 0 && a.applicability !== "Exempt/Not Applicable" ? (
            <span className="flex items-center gap-1.5 shrink-0" title={missing.length ? T("必需文件缺：{0}", [missing.map((x) => TB(x)).join(getLang() === "zh" ? "、" : ", ")]) : T("必需文件齐全")}>
              <span className="flex gap-px">{docs.map((x) => <span key={x} className={`h-1 w-3 rounded-full ${have.has(x) ? "bg-emerald-500" : "bg-slate-300"}`} />)}</span>
              <span className="text-[10px] text-slate-400">{T("必需")}</span>
              <span className={`text-[10px] tabular-nums font-semibold ${missing.length ? "text-amber-700" : "text-emerald-700"}`} data-testid={`dv3-agency-docs-${a.id}`} data-missing={missing.length}>{docs.length - missing.length}/{docs.length}</span>
            </span>
          ) : <span className="text-[10px] text-slate-400">{T("无必需文件清单")}</span>}
          <div className="ml-auto flex items-center gap-1 shrink-0">
            {/* 紧凑模式同样只读：理由同上方非紧凑模式的注释（全流程责任已全局配置办理身份） */}
            {(() => {
              const names = (resp[a.type] || {}).handleNames || [];
              const fallback = a.owner ? (designers.find((u) => u.username === a.owner)?.name || a.owner) : "";
              const shown = names.length ? names.join("、") : fallback;
              return <span className={`text-[10px] truncate max-w-[76px] ${shown ? "text-slate-500" : "text-slate-300"}`} title={T("办理人由「全流程责任」按身份全局配置（{0}）· 如需换人请改那边", [a.type])} data-testid={`dv3-agency-owner-${a.id}`}>{shown || T("未指派")}</span>;
            })()}
            {meta.canEdit && <span className="relative"><input ref={inp} type="file" className="hidden" accept={DESIGN_UPLOAD_ACCEPT} data-testid={`dv3-agency-upload-input-${a.id}`} onChange={(e) => { upload(e.target.files?.[0], pendingDt.current); pendingDt.current = ""; e.target.value = ""; }} /><button type="button" data-testid={`dv3-agency-upload-${a.id}`} title={T("上传附件（回执 / 批文 / 报告）")} onClick={() => startUpload("")} className="h-5 w-5 inline-flex items-center justify-center rounded text-slate-400 hover:bg-white hover:text-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60"><UploadCloud className="h-3.5 w-3.5" /></button></span>}
            <button type="button" data-testid={`dv3-agency-files-${a.id}`} onClick={() => setShowFiles(!showFiles)} className={`relative h-5 w-5 inline-flex items-center justify-center rounded hover:bg-white ${liveFiles.length ? "text-violet-700" : "text-slate-300"}`} title={T("附件（有效 {0} / 共 {1}）", [liveFiles.length, files.length])}><Paperclip className="h-3.5 w-3.5" />{liveFiles.length > 0 && <span className="absolute -top-1 -right-1 bg-violet-600 text-white rounded-full text-[8px] w-3.5 h-3.5 flex items-center justify-center">{liveFiles.length}</span>}</button>
          </div>
        </div>
      ) : docs.length > 0 && a.applicability !== "Exempt/Not Applicable" && (
        // MEP：一家机构、三份资料 —— 详情页也按给排水 / 暖通 / 电气分开报缺口，
        // 与会话抽屉右侧同一份分组（meta.mepDocGroups，槽名 = 上传 docType）。
        a.type === "Plumbing/Mechanical/Electrical" && (meta.mepDocGroups || []).length > 0 ? (
          <div className="mt-1 space-y-0.5" data-testid={`dv3-agency-mep-docs-${a.id}`}>
            {(meta.mepDocGroups || []).filter((g) => !g.optional).map((g) => {
              const gs = g.slots.filter((s) => docs.includes(s));
              if (!gs.length) return null;
              const gm = gs.filter((s) => !have.has(s));
              return (
                <p key={g.key} className={`text-[10px] flex flex-wrap items-center gap-1 ${gm.length ? "text-amber-700" : "text-emerald-700"}`}
                  data-testid={`dv3-agency-mep-docs-${a.id}-${g.key}`} data-missing={gm.length}>
                  <ListChecks className="h-3 w-3" />{T(g.zh)} {T("已收 {0}/{1}", [gs.length - gm.length, gs.length])}
                  {gm.length ? <> {T("· 缺：{0}", [gm.map(TB).join(getLang() === "zh" ? "、" : ", ")])}</> : T(" · 齐全")}
                </p>
              );
            })}
          </div>
        ) : (
          <p className={`text-[10px] mt-1 flex flex-wrap items-center gap-1 ${missing.length ? "text-amber-700" : "text-emerald-700"}`} data-testid={`dv3-agency-docs-${a.id}`} data-missing={missing.length}>
            <ListChecks className="h-3 w-3" />{T("必需文件 已收 {0}/{1}", [docs.length - missing.length, docs.length])}{missing.length ? <> {T("· 缺：{0}", [missing.map((x) => TB(x)).join(getLang() === "zh" ? "、" : ", ")])}</> : T(" · 齐全")}
          </p>
        )
      )}
      {!compact && (a.completedAt || a.statusAt) && <p className="text-[10px] text-slate-500 mt-1" data-testid={`dv3-agency-meta-${a.id}`}>{done ? <><CheckCircle2 className="h-3 w-3 inline text-emerald-600" /> {T("已完成 · {0} · {1}", [a.completedBy || a.statusBy || "—", d10(a.completedAt || a.statusAt)])}</> : <>{T("最近操作 {0} · {1} · {2}", [AG_ZH[a.status] || a.status, a.statusBy || "—", d10(a.statusAt)])}</>}{a.dueDate ? T(" · 节点截止 {0}", [d10(a.dueDate)]) : ""}</p>}
      {showFiles && (
        <div className="mt-1.5 space-y-1" data-testid={`dv3-agency-filelist-${a.id}`}>
          {files.length === 0 && <p className="text-[10px] text-slate-400">{T("暂无附件")}</p>}
          {sortedFiles.map((f, i) => (
            <React.Fragment key={f.aid}>
            <div onDoubleClick={() => setLb(i)} title={T("双击预览 · 右键更多操作")} {...ctx.bind(menuFor(f, i))} className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] bg-white rounded-md border border-slate-100 px-2 py-1 select-none cursor-pointer hover:bg-violet-50/60 ${f.voided ? "opacity-60" : ""}`} data-testid={`dv3-agency-file-${f.aid}`} data-voided={f.voided ? "1" : "0"}>
              <span className={`text-[9px] px-1 rounded font-bold ${f.voided ? "bg-slate-200 text-slate-400" : "bg-slate-100 text-slate-500"}`}>v{f.version}</span>
              {/* 归类标签：已归类显示清单项名（绿）；未归类显示可点的「未归类」（琥珀）——
                  上传改成一步到位后，这里就是提醒「这份还没对上清单项」的唯一落点。 */}
              {f.docType
                ? <span className={`text-[9px] px-1 rounded font-semibold truncate max-w-[120px] ${f.voided ? "bg-slate-100 text-slate-400" : "bg-emerald-50 text-emerald-700"}`} title={f.docType}>{f.docType}</span>
                : !f.voided && meta.canEdit && docs.length > 0 && (
                  <button type="button" onClick={(e) => { e.stopPropagation(); setPickFor(pickFor === f.aid ? null : f.aid); }}
                    className="text-[9px] px-1 rounded font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 shrink-0"
                    title={T("点击归到必需文件清单项")} data-testid={`dv3-agency-file-doctype-${f.aid}`}>{T("未归类")}</button>
                )}
              <button type="button" onClick={() => setLb(i)} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 truncate flex-1 basis-[120px] min-w-[120px] text-left ${f.voided ? "line-through text-slate-400" : "text-violet-700 hover:underline"}`} title={f.voided ? T("已作废（仍可预览留档）：{0}", [f.voidReason || ""]) : T("预览（可翻页 / 缩放）")} data-testid={`dv3-agency-file-open-${f.aid}`}>{f.name}</button>
              <button type="button" onClick={() => downloadFile(f.fileId, f.name).catch((e) => toast.error(e?.message || T("下载失败")))} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center justify-center text-slate-400 hover:text-violet-700 shrink-0" title={T("下载")} data-testid={`dv3-agency-file-download-${f.aid}`}><FileDown className="h-3 w-3" /></button>
              {f.voided && <span className="text-[9px] px-1 rounded bg-red-50 text-red-600 font-semibold shrink-0" title={f.voidReason || ""} data-testid={`dv3-agency-file-voided-${f.aid}`}>{T("已作废")}</span>}
              <span className="text-slate-400 basis-full sm:basis-auto sm:ml-auto">{T("上传者：{0}", [f.uploaderName || f.uploader])}{f.voided ? ` · ${T("作废：{0}", [f.voidReason || ""])}` : ""}</span>
              <FileUploadDate value={f.uploadedAt} fallbackValue={f.at} className="basis-full" />
            </div>
            {/* 清单项归类弹层：贴在对应文件行下方（不再浮在上传按钮上），
                上传完成后自动展开同一份弹层，行为一致。
                data-testid 沿用原有 `dv3-agency-docpick-{机构}-{序号}` / `-other`，
                不因交互改版而改契约，旧 E2E 仍可定位。 */}
            {pickFor === f.aid && (
              <div className="relative">
                <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-1.5 w-56 space-y-0.5" data-testid={`dv3-agency-docpick-${a.id}`}>
                  <p className="text-[10px] text-slate-400 px-1">{T("这份文件对应清单项：")}</p>
                  {docs.map((x) => (
                    <button key={x} type="button" data-testid={`dv3-agency-docpick-${a.id}-${docs.indexOf(x)}`} onClick={() => setDocType(f.aid, x)}
                      className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 w-full text-left text-[11px] px-2 py-1 rounded hover:bg-violet-50 flex items-center gap-1.5">
                      {have.has(x) ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <span className="h-3 w-3 rounded-full border border-slate-300" />}
                      <span className={have.has(x) ? "text-slate-400" : "text-slate-800"}>{x}</span>
                    </button>
                  ))}
                  <button type="button" data-testid={`dv3-agency-docpick-${a.id}-other`} onClick={() => setDocType(f.aid, "")}
                    className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 w-full text-left text-[11px] px-2 py-1 rounded hover:bg-slate-50 text-slate-500">{T("其他 / 不对应清单项")}</button>
                  <button type="button" onClick={() => setPickFor(null)} className="w-full text-left text-[10px] px-2 py-1 rounded hover:bg-slate-50 text-slate-400">{T("稍后再说")}</button>
                </div>
              </div>
            )}
            </React.Fragment>
          ))}
        </div>
      )}
      {lb !== null && sortedFiles[lb] && <MediaLightbox tid="dv3-agency-lightbox" index={lb} onIndex={setLb} onClose={() => setLb(null)} api={api} zh
        items={sortedFiles.map((f) => ({ id: f.aid, fileId: f.fileId, size: f.size, url: fileUrl(f.fileId), kind: mediaKind(f.name, f.contentType), name: `v${f.version} · ${f.name}`, meta: `${f.docType ? `${f.docType} · ` : ""}${f.uploaderName || f.uploader} · ${uploadDateLabel(f.uploadedAt, f.at)}` }))} />}
      {ctx.element}
    </div>
  );
};

// ---------- 阶段任务页签 ----------
export const StageTasksTab = ({ pid, meta, p, refresh, onStageComments }) => {
  const commentCount = (...keys) => (p?.comments || []).filter((c) => keys.includes(c.stageKey)).length;
  const CommentBadge = ({ keys, tid }) => { const n = commentCount(...keys); return n ? <button type="button" onClick={(e) => { e.stopPropagation(); onStageComments?.(keys.join(",")); }} title={T("该阶段有 {0} 条评论 · 点击查看", [n])} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100" data-testid={tid} data-count={n}><MessageSquareText className="h-3 w-3" />{n}</button> : null; };
  const { api, lang } = useApp();
  const { d, lmeta, load } = useLegacyDetail(pid);
  const { nodes, reloadNodes } = useNodes(pid, p);
  /* 2026-10-19 用户需求：项目管理（签约后）详情不再展示签约前的「前期方案咨询 / 合同签署」。
     这里只过滤阶段页签的展示数组，数据库里的阶段实例、子任务、门禁和付款数据保持不变；
     签约前营销方案页（p.phase === "pre_sign"）仍完整展示这两步。 */
  const shownStages = useMemo(
    () => (d?.stages || []).filter((s) => !isStageHiddenInProjects(s.key, p?.phase)),
    [d, p?.phase],
  );
  const [sel, setSel] = useState(null);
  const [filesTask, setFilesTask] = useState(null);
  const [qaTask, setQaTask] = useState(null);
  const [gantt, setGantt] = useState(false);
  const [bulk, setBulk] = useState({});
  const [handoffAsk, setHandoffAsk] = useState(false);
  const [memberToClose, setMemberToClose] = useState(null);
  const [taskToReconcile, setTaskToReconcile] = useState(null);
  const [taskReconcileBusy, setTaskReconcileBusy] = useState(false);
  const [stageCloseBusy, setStageCloseBusy] = useState(false);
  const [stageOverride, setStageOverride] = useState(null);
  const stageOverrideResolve = useRef(null);
  useEffect(() => () => {
    stageOverrideResolve.current?.(null);
    stageOverrideResolve.current = null;
  }, []);
  const [dropping, setDropping] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  /* 配对产物审核动作的进行中标记（2026-10-21）：产物卡收起后，审核通过 / 退回重做
     改在任务卡上点。记任务 id（而不是产物 key），因为一张任务卡对应一个产物，
     按钮就在这张卡上转圈，用户不会误以为点的是别的卡。 */
  const [pairBusy, setPairBusy] = useState(null);
  /* 子任务卡比产物卡重（标题 + 状态下拉 + 元信息 + 拖拽提示 + 操作条），单卡 320px 起，
     否则「负责人下拉 + 4 个图标」会换行、标题被截成一两个字。 */
  const [tCols, bindTaskGrid] = useGridCols(320, 4);
  const agMap = useDiscAgencyMap(api);
  const agToDisc = useMemo(() => reverseAgencyMap(agMap), [agMap]);
  const designers = meta.designers || [];
  const nameOf = (un) => designers.find((u) => u.username === un)?.name || un || "";
  /* 指派候选（2026-10-19 用户需求）：阶段主负责 / 批量指派 / 子任务负责人
     都只能从项目负责人里选（US / CN / 交图员 / 协作负责人），不再罗列设计部全部同事。
     历史负责人若已不在负责人列表，仍保留一项并标「历史」，避免下拉错显成未指派。 */
  const ownerOpts = (current) => projectOwnerOptions(p, designers, current);
  const ownerHint = T("请先在矩阵「负责人」列设置 US / CN / 交图员或协作负责人");
  const reloadAll = () => { load(); reloadNodes(); refresh?.(); };
  const reconcileTaskProgress = async (reason) => {
    if (!taskToReconcile || reason.trim().length < 4) {
      toast.error(T("请填写纠正原因（至少 4 个字）"));
      return;
    }
    setTaskReconcileBusy(true);
    try {
      await api.post(`/design/tasks/${taskToReconcile.id}/reconcile-progress`, { reason });
      toast.success(T("任务状态已纠正为进行中"));
      setTaskToReconcile(null);
      reloadAll();
    } catch (e) {
      toast.error(errTextOf(e?.response?.data?.detail || e.message));
    } finally {
      setTaskReconcileBusy(false);
    }
  };
  const openStage = (key) => { setSel(key); setTimeout(() => document.querySelector(`[data-testid="dv3-stage-${key}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };
  const deepStage = useRef(canonicalStageKey(new URLSearchParams(window.location.search).get("stage")));
  // 深链 ?stage=xx 只在阶段仍可见时展开；被隐藏的签约前阶段不抢焦点，仍保留在 URL 里不影响其它页签。
  useEffect(() => { if (shownStages.length && deepStage.current) { const k = deepStage.current; deepStage.current = null; if (shownStages.some((s) => s.key === k)) openStage(k); } }, [shownStages]); // eslint-disable-line
  /* 【2026-10-17 卡片化】点上方节点 = 只显示该阶段。首屏默认落到当前活跃阶段，
     避免一进来是一大片空白；只初始化一次，之后用户收起（sel=null）不再强行抢回来。
     2026-10-19：当前阶段若恰好是被隐藏的签约前阶段，则回退到第一个可见阶段。 */
  const selInit = useRef(false);
  useEffect(() => {
    if (selInit.current || !shownStages.length) return;
    selInit.current = true;
    const cur = canonicalStageKey(d?.project?.currentStage);
    setSel(shownStages.some((s) => s.key === cur) ? cur : shownStages[0].key);
  }, [d, shownStages]); // eslint-disable-line
  /* 【2026-10-21 修复】阶段完成后的「现在移交施工吗」提示。
     一开始这段逻辑挂在手动「关闭阶段」按钮的成功回调里；按钮当天被移除后，阶段改由后端
     资料齐全自动推进，前端一度没有「阶段完成」这个回调点，所以改成现在这种「状态变化即提示」。
     2026-10-21 傍晚按钮已按用户要求恢复（改名「完成阶段」），但这里**仍保留状态监听**：
     自动推进也一样会把 permit_handoff 标成完成，两条路都必须提醒到，不能只挂在某个按钮上。
     保留这段提示的意义：最后一个阶段叫「第三笔款 + 移交施工」，阶段完成 ≠ 项目已交给
     工程部（stage_close 只把阶段标 Completed）。不提示的话经理会以为已经交完，
     工程部那边永远看不到项目。现在：本阶段由「未完成」变为「已完成」时，
     只要它是 permit_handoff、项目还没移交、当前用户是经理，就提示一次。
     用 ref 记住已提示过的阶段，避免父组件重渲染时反复弹窗。

     ⚠️ 必须放在下面三个 early return **之前**：这些 return（加载中 / 出错 / 无阶段链）
     会让同一个组件在「有阶段」和「没阶段」两种渲染间切换 hooks 数量，
     React 会直接抛 "Rendered more hooks than during the previous render" 把整个
     详情抽屉炸成白屏（2026-10-21 实测：打开没有阶段链的项目必崩）。 */
  const handoffPrompted = useRef(new Set());
  useEffect(() => {
    if (!meta?.isManager || isClosedProject(p) || !d) return;
    const ph = (d.stages || []).find((x) => x.key === "permit_handoff");
    const done = ph && (ph.status === "Completed" || ph.actualFinish);
    if (!done || handoffPrompted.current.has("permit_handoff")) return;
    handoffPrompted.current.add("permit_handoff");
    if (window.confirm(T("阶段已完成。现在把项目移交给工程部吗？\n\n不现在移交也没关系，稍后可在「概览」点「确认移交施工」。"))) setHandoffAsk(true);
  }, [d, meta?.isManager, p]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (d.error) return <p className="text-sm text-red-600" data-testid="dv3-stages-error">{errTextOf(d.error)}</p>;
  if (!d.stages.length) return <InstantiatePanel pid={pid} meta={meta} api={api} project={d.project} onDone={reloadAll} />;
  const allTasks = shownStages.flatMap((s) => s.tasks || []);
  const freshFiles = filesTask ? allTasks.find((t) => t.id === filesTask.id) || filesTask : null;
  const freshQa = qaTask ? allTasks.find((t) => t.id === qaTask.id) || qaTask : null;
  const mode = (p?.approvalMode || d.project?.approvalMode || "sequential") === "concurrent" ? "concurrent" : "sequential";
  const groups = groupStages(shownStages, mode, meta.stageTemplate);
  const nmap = groupNodes(nodes, groups);
  const cardContents = Object.fromEntries(groups.map((g) => {
    const stage = g.merged ? mergeStage(g) : g.stages[0];
    return [g.key, { stage, ...stageCardContents(stage, nmap[g.key], d.agencies, agToDisc) }];
  }));
  const currentStageKey = canonicalStageKey(d.project?.currentStage);
  const currentStage = groups.some((s) => (s.stages || [s]).some((m) => m.key === d.project?.currentStage)) ? currentStageKey : undefined;
  /* 【2026-10-23 三态位置化】把「显示顺序上的整条流程」算一遍三态，阶段卡直接取用。
     为什么要在这一层算：一张阶段卡自己看不出「前面的阶段收口了没有」，而
     「进行中」正是由这个位置决定的（口径见 meta.js stagePhasesOf）。
     只对**展示出来的阶段**算（shownStages 已按项目 phase 过滤掉签约前两列），
     否则被隐藏的 contract 未完成会把后面所有阶段都压成「未开始」。
     currentStage 依然只用于「当前」台账标记与顺序门禁置灰，不参与三态。 */
  const { phaseOf: stagePhaseMap, nodePhaseOf: nodePhaseMap } = stagePhasesOf(groups.map((g) => ({
    key: g.key,
    members: (g.stages || [g]).map((m) => ({ key: m.key, status: m.status, actualFinish: m.actualFinish })),
  })));
  const phaseOfStageKey = (key, fallback) => stagePhaseMap[key] || nodePhaseMap[key] || fallback || STAGE_PHASE.not_started;
  const flowTemplateKeys = (meta.stageTemplate || []).map((x) => x.key);
  const flowFrontier = flowFrontierOf(d.stages || [], d.project, flowTemplateKeys);
  const isTooEarlyToClose = (stage) => stageTooEarlyOf(
    flowOrderOf(stage.key, stage.order, d.project, flowTemplateKeys), flowFrontier,
  );
  const setMode = async (m) => {
    try { await api.patch(`/design/v3/projects/${pid}`, { approvalMode: m }); toast.success(T("审批模式已切换为「{0}」· 阶段任务不变，仅显示合并", [T(APPROVAL_MODE_ZH[m])])); reloadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  /* 原来这里是 setTaskStatus()：阶段卡里的任务状态下拉直接 PATCH 任务状态。
     2026-10-21 用户要求「这个选项不需要了……都是必传才能进行下一个步骤」后，
     任务状态改由上传 / 产物审核驱动，前端不再提供手选入口，函数一并删除。
     后端 PATCH /design/tasks/{id} 仍保留：旧页面、自动化与「本阶段不适用」场景还要用。 */
  const setTaskOwner = async (t, who) => { await api.patch(`/design/tasks/${t.id}`, { primaryOwner: who || null }); reloadAll(); };
  const bulkAssign = async (s) => {
    const owner = bulk[s.key]; if (!owner) return toast.error(T("请先选择负责人"));
    /* 【2026-10-22 P+B 合并】合并节点要按成员阶段逐个指派：/design/stages/{id}/assign
       只认单个阶段 id，节点键（pb_set）在库里不存在，直接发会 404。 */
    const targets = stageTargets(s, true);
    let assigned = 0;
    try {
      for (const t of targets) {
        const { data } = await api.post(`/design/stages/${t.id}/assign`, { owner });
        assigned += data.assigned || 0;
      }
      toast.success(assigned ? T("已把「{0}」{1} 项未指派子任务指派给 {2}", [T(s.name), assigned, ownerOpts("").find((u) => u.username === owner)?.name || owner]) : T("该阶段没有未指派的子任务"));
      reloadAll();
    }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const dropFiles = async (t, fileList) => {
    const arr = Array.from(fileList || []); if (!arr.length) return;
    setUploadingId(t.id);
    const have = (t.files || []).map((f) => f.type);
    /* 越级上传（2026-10-23 用户需求「阶段限制时后面的阶段依然可以让先将资料产物等先上传上去...
       但是不会改变阶段显示的状态」）：后端放行但跳过所有状态推进，提示里如实说明。 */
    let deferred = false;
    let started = false;
    try {
      for (const f of arr) {
        const up = await uploadChunked(api, f, { shrink: false });
        const fd = new FormData();
        appendUploadRef(fd, up, f);
        fd.append("type", inferFileType(f.name, t.requiredFileTypes || [], have));
        fd.append("status", "Draft");
        const { data } = await api.post(`/design/tasks/${t.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.deferred) deferred = true;
        if (data?.taskStarted) started = true;
        // 合同槽位的门牌软校验（只提示不拦截）
        showContractWarnings(data?.warnings);
      }
      /* 2026-10-23：越级上传现在会把**任务卡自己的状态**从「未开始 / 就绪」推进到
         「进行中」（后端 task_upload 写 In Progress），但阶段状态仍按流程位置不动。
         提示必须把这两件事分开说，否则用户会以为「说没变、可卡片明明变了」。 */
      toast.success(deferred
        ? (started
          ? T("已存档 {0} 个文件到「{1}」· 任务已开始，阶段流程还没走到", [arr.length, T(t.title)])
          : T("已存档 {0} 个文件到「{1}」（流程还没走到本阶段，阶段状态未变）", [arr.length, T(t.title)]))
        : T("已上传 {0} 个文件到「{1}」", [arr.length, T(t.title)]));
      reloadAll();
    } catch (e) { toast.error(uploadErrText(e, true)); }
    setUploadingId(null);
  };
  /* 产物的交图复核结论（2026-10-21）：产物卡收起后，「审核通过 / 退回重做」搬到了
     任务卡上（见 renderStage 里的 dv3-ltask-pairdisc-* 按钮）。
     走的是既有的 POST /design/v3/reviews/{rid}/decide —— 它内部会：
       · 通过 → 产物置 done → 配对任务自动收口 → 阶段门禁放行 → 自动进入下一步；
       · 退回 → 产物置整改 + 派返工任务。
     不在前端自己拼状态，避免漏掉这些副作用（与产物页签同一入口）。 */
  const decidePair = async (t, rev, result) => {
    if (!rev?.id) return;
    let comment = "";
    if (result === "needs_fix") {
      comment = (window.prompt(T("请填写修改意见（将自动派一次返工任务给提交人）")) || "").trim();
      if (!comment) return;
    }
    setPairBusy(t.id);
    try {
      const { data } = await api.post(`/design/v3/reviews/${rev.id}/decide`, { result, comment });
      toast.success(T("已复核 · 从交图到复核耗时 {0} 小时", [data.hours]));
      (data.automation || []).forEach((a) => toast.message(T("自动化 {0}", [a.rule.toUpperCase()]), { description: a.text }));
      reloadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setPairBusy(null);
  };
  const reschedule = async (t) => {
    /* 【2026-10-30 输入侧时区提示】用户要求「设计部整个都是国外时间显示，国内设置时间
       时要有提示让用户知道要设置国外时间」。改期值会经 usDayStartIso 按洛杉矶日历日
       落库（见下面 2026-10-29 注释），中国同事若按北京时间算会差一天。
       这个入口是 window.prompt，没有版面挂 <UsDateHint />，提示只能写进问句本身。 */
    const due = window.prompt(T("新到期日 (YYYY-MM-DD)") + "\n\n" + T(US_DATE_TIP), dateInputValue(t.dueDate)); if (!due) return;
    const reason = window.prompt(T("改期原因（必填）")); if (!reason) return toast.error(T("必须填写改期原因"));
    /* 【2026-10-29】提交洛杉矶零点瞬间：原来用 dayStartIso（浏览器本地零点），
       中国同事选的 10/24 落库后美国同事回看会变成 10/23。统一口径后一律按美国日历日。 */
    try { await api.patch(`/design/tasks/${t.id}`, { dueDate: usDayStartIso(due), delayReason: reason }); toast.success(T("已改期（保留基线）")); reloadAll(); } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const rescheduleStage = async (s) => {
    /* 【2026-10-30 输入侧时区提示】同 reschedule：改期值按洛杉矶日历日落库，
       而 window.prompt 挂不了组件，所以提示进文案。 */
    const due = window.prompt(T("「{0}」新截止日 (YYYY-MM-DD)", [T(s.name)]) + "\n\n" + T(US_DATE_TIP), dateInputValue(s.dueDate)); if (!due) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) return toast.error(T("日期格式 YYYY-MM-DD"));
    const reason = window.prompt(T("修改原因（必填，保留基线）")); if (!reason) return toast.error(T("必须填写原因"));
    /* 【2026-10-22 P+B 合并】合并节点改截止日 = 组内全部未完成成员阶段一起改。
       原因：节点只显示一个截止日（取最晚），改一半会出现「节点写着 11-01、
       成员里还有一条停在 10-15」的自相矛盾状态；而且改期会同步平移未完成子任务，
       只改一个成员会让 P+B 两条链的截止日错开，用户就再也看不出「这是一套图」。
       已完成的阶段不动（历史截止日不该被事后改写）。
       【时区 2026-10-29 统一】due 是人选的**美国日历日**，后端一律按洛杉矶解释
       （design_module.tz_day_start 不再看提交者时区），所以这里不再传 tz。
       旧口径（2026-10-22）是「中国选的 10/24 美国看 10/23」，已按用户要求废止。 */
    try {
      const targets = stageTargets(s, true);
      let shifted = 0;
      let cascaded = 0;
      /* 【2026-10-27 后端已改为「改截止日自动级联后续阶段」】
         用户口径：「所有的改期和合理延时都应该让后续阶段自动延时」。
         后端 stage_update 现在会把本阶段之后的未完成阶段一起平移，因此这里要防重复：
         合并节点（P+B）是多成员逐个 PATCH，若每个都触发级联，同一个后续阶段会被
         平移 N 次（N = 组内成员数），日期越飘越远。所以除最后一项外都传 cascade:false，
         只在最后一次让后端真正级联。单阶段（非合并）时 targets 只有 1 项，等同默认级联。 */
      for (let i = 0; i < targets.length; i += 1) {
        const t = targets[i];
        const isLast = i === targets.length - 1;
        const { data } = await api.patch(`/design/stages/${t.id}`,
          { dueDate: due, delayReason: reason, cascade: isLast });
        shifted += data.shiftedTasks || 0;
        cascaded += data.cascadedStages || 0;
      }
      /* 提示要写清后续也被改了：用户改的是这一格，实际动的是整条后续链，
         不说明会以为只有本阶段变了。 */
      toast.success(T("阶段截止日已改为 {0}{1}{2}", [
        due,
        cascaded ? T(" · 后续 {0} 个阶段及其子任务同步顺延", [cascaded]) : "",
        shifted ? T(" · 共 {0} 项未完成子任务更新", [shifted]) : "",
      ]));
      reloadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  /* 关闭阶段（2026-10-21 恢复，用户补充需求）。
     背景：当天早些时候按用户「所有资料齐全时自动进行到下一步」的口径，把这里的「关闭阶段」
     按钮连同 closeStage() 一起删掉了，推进改由后端 maybe_auto_advance_stage 在资料变化时
     自动触发。用户随后明确「这个每个阶段的关闭按钮还是需要，这个关闭阶段其实就是完成阶段
     的意思」——他要的是「人工确认这一阶段做完了」这件事本身，而不只是流程往下走。
     为什么两者必须并存（不是重复）：
       · 自动推进只对**项目当前阶段**生效，且要求产物门禁 + 子任务门禁全过。真实项目里
         常有「本单不做某项产物 / 某项审批改为豁免」的情形，自动推进不会替人做这个判断，
         阶段就会停在那里；人点一下「完成阶段」是唯一能收口的动作。
       · 反过来，资料齐了的阶段由自动推进收口，不必等人来点 —— 两条路复用同一份后端
         stage_close_core，副作用（门禁、子任务收口、激活下一阶段、付款到期）完全一致，
         不会出现两种关法数据不一样。
     保留原有门禁口径：产物未全部审核通过 / 缺必需文件时后端 400，经理可填原因强关
     （至少 4 个字；原因记录在阶段，部分门禁另写项目动态并通知设计经理）。 */
  const requestStageOverride = (stage, message) => new Promise((resolve) => {
    stageOverrideResolve.current = resolve;
    setStageOverride({ stage, message });
  });
  const settleStageOverride = (reason) => {
    const resolve = stageOverrideResolve.current;
    stageOverrideResolve.current = null;
    setStageOverride(null);
    resolve?.(reason);
  };
  const closeStage = async (s, override, overrideReason) => {
    /* 【2026-10-22 P+B 合并·第四轮】「完成该节点」= 按组内书写顺序**逐个关闭全部未完成成员**。
       用户口径：「在 p+b 模式中的节点中铺开...东西都要」「他们就是一个图了..改就全改」——
       节点按钮仍逐个关闭所有未完成成员；成员按钮则只传入选中的真实阶段，供补记使用。
       顺序按 s.stages 的书写顺序（= CONCURRENT_GROUPS 成员顺序，与后端 correction_pass 一致），
       这样 currentStage 的推进轨迹与 City「已通过」完全一样。
       已完成的成员跳过（幂等，重复点不会 400）；普通阶段 s.stages 就是它自己，行为不变。

       错误取值用 detailRaw ?? detail（main 侧 2026-10-22 的源头治理）：结构化 detail
       会被 lib/detailText 摊平成字符串、原对象存进 detailRaw，这里要读原对象才能
       分辨 gate_files_missing / stage_products_not_approved 等分支。 */
    const targets = stageTargets(s, true);
    if (!targets.length) return;
    let done = 0;
    const reportPartial = () => {
      if (done) { toast.message(T("已关闭 {0} 个阶段，剩余阶段未完成", [done])); reloadAll(); }
    };
    for (const t of targets) {
      try {
        await api.post(`/design/stages/${t.id}/close`, override ? { override: true, overrideReason: overrideReason || "" } : {});
        done += 1;
      } catch (e) {
        const det = e?.response?.data?.detailRaw ?? e?.response?.data?.detail;
        if (det?.error === "gate_files_missing" || det?.error === "stage_products_not_approved") {
          const missingFiles = det.error === "gate_files_missing";
          const list = missingFiles
            ? (det.detail || []).map((x) => `${T(x.task)}: ${(x.missing || []).map((type) => T(type)).join("、")}`).join("\n")
            : (det.detail || []).map((x) => `${T(x.name)}${x.zh ? `（${T(x.zh)}）` : ""}${x.reviewPending ? ` · ${T("待审核")}` : ""}${x.reviewReason ? ` · ${T(x.reviewReason)}` : ""}`).join("\n");
          const message = `${missingFiles ? T("缺少必需文件：") : T(det.message || "本阶段还有产物未完成审核。")}\n${list}`;
          const reason = meta.isManager ? await requestStageOverride(t, message) : null;
          if (!reason) {
            toast.error(missingFiles ? T("缺少必需文件，无法完成阶段") : T("产物未通过审核，无法完成阶段"));
            reportPartial();
            return;
          }
          try {
            await api.post(`/design/stages/${t.id}/close`, { override: true, overrideReason: reason });
            done += 1;
            continue;
          } catch (e2) {
            toast.error(e2?.response?.data?.detailRaw?.message || e2?.response?.data?.detail?.message || e2.message);
            reportPartial();
            return;
          }
        } else if (det?.error === "stage_order_violation") {
          /* 流程顺序门禁（2026-10-22）：阶段必须按 order 推进，没轮到本阶段不能先完成。
             按钮已按同一判定置灰，这里兜住「页面数据过期 / 直接调接口」的情况。
             不提供强关入口 —— 越级完成会让后面的阶段全部错过，不是可以用理由覆盖的例外。 */
          toast.error(det.message || T("流程还没走到本阶段，请先完成当前阶段"));
        } else if (det?.error === "override_reason_required") {
          toast.error(det.message || T("必须填写强行完成阶段的原因"));
        } else toast.error(typeof det === "string" ? det : e.message);
        // 任一成员失败就停：剩下的成员继续关会让节点进入更难解释的半完成态。
        reportPartial();
        return;
      }
    }
    toast.success(s.merged
      ? T("节点已完成（{0} 个阶段一起关闭），下一节点激活", [done])
      : mode === "concurrent" && concurrentGroupOf(s.key)
        ? T("子阶段已完成")
        : T("阶段已完成，下一阶段激活"));
    reloadAll();
    // 【2026-10-17】阶段链最后一个阶段叫「第三笔款 + 移交施工」，但关闭它**不会**真的把项目
    // 交给工程部（后端 stage_close 只标 Completed）。不提示的话，经理会以为关了就交完了，
    // 工程部那边永远看不到项目。关闭成功后直接问一句，确认就打开移交弹窗。
    if ((s.merged ? s.stages.map((m) => m.key) : [s.key]).includes("permit_handoff") && !isClosedProject(p) && meta.isManager) {
      if (window.confirm(T("阶段已完成。现在把项目移交给工程部吗？\n\n不现在移交也没关系，稍后可在「概览」点「确认移交施工」。"))) setHandoffAsk(true);
    }
  };
  const runCloseStage = async (stage) => {
    if (stageCloseBusy) return;
    setStageCloseBusy(true);
    try { await closeStage(stage); }
    catch (e) { toast.error(e?.message || T("完成阶段失败")); }
    finally { setStageCloseBusy(false); }
  };
  /* 回退阶段（2026-10-23 用户需求）。
     需求原话：「这个项目需要回退到方案设计阶段，505 也是」（配两张矩阵截图，红框都在
     「方案设计」的已完成格）。此前系统只有项目级「撤销结项」，阶段完成后退不回来。

     交互设计（为什么用两次 prompt 而不是新做一个弹窗组件）：
       本项目阶段卡上既有的破坏性操作用的就是 window.confirm + window.prompt 组合
       （见 closeStage 的强关原因、rescheduleStage 的改期原因）。这里沿用同一套，
       保证「同一个页面里所有需要填原因的动作用同一种交互」，不引入新的弹窗状态、
       也不必改本组件的 hooks 结构（多一个 useState 就要处理早退分支，反而更脆）。

     默认口径（与后端一致，把最稳的选项放在默认）：
       · 只回退本阶段，**不动下游** —— 用户在截图里只圈了「方案设计」一格；
         ADU-0119 的业主签字已完成，默认不碰它。
       · 产物退回「制作中」，但**文件与版本历史全部保留** —— 回退是接着改，不是抹掉历史；
         重新提交时版本号自动递增为 v2，v1 仍能在「图纸版本」里查到。
       · 为什么产物要跟着退：本地实测发现只退阶段时，产物仍显示「已完成 1/1」，
         阶段产物门禁立刻又通过，「退了却一按就能立刻关回去」，完全看不出要重做。

     合并节点（P+B）按组内顺序逐个回退：与 closeStage 的 stageTargets 对称，
     避免「同一套图只退一半」的半完成态。
     */
  const reopenStage = async (s) => {
    /* 注意 openOnly 必须是 false：stageTargets(s, true) 的语义是「跳过已完成成员」
       （供「完成该节点」用），而回退要的恰好是**已完成**的那几个 —— 传 true 会得到空数组。
       这里取全部成员再按「已完成口径」筛，与后端 stage_reopen 的可回退状态集合一致。 */
    const targets = stageTargets(s, false).filter((t) => REOPENABLE.includes(t.status));
    if (!targets.length) { toast.error(T("该节点内没有已完成的阶段，无需回退")); return; }
    const label = s.merged
      ? T("节点「{0}」内的 {1} 个已完成阶段", [T(s.name), targets.length])
      : T("阶段「{0}」", [T(s.name)]);
    if (!window.confirm(T("把{0}退回「进行中」？\n\n本阶段的产物会退回「制作中」，但已上传的图纸、附件与版本历史全部保留，重新提交时版本号自动递增为 v2。", [label]))) return;
    const reason = (window.prompt(T("请填写回退原因（至少 4 个字，会记录在项目动态里）"), "") || "").trim();
    if (reason.length < 4) { toast.error(T("必须填写原因（至少 4 个字）")); return; }
    /* 下游已完成阶段（2026-10-23 补）：默认只退未完成的，但这些"已完成"的留在后面会让
       矩阵出现「进行中 → 已完成 → 未开始」的自相矛盾（ADU-0059 用户实测反馈）。
       所以在问「连带退回」之前，先把它们的名字与完成日期摆出来，让人自己决定。
       【2026-10-24 与显示同源】「下游」按当前模板层号算（与后端 stage_reopen 的
       cascade 同口径）：存量项目旧 order 里 building_paperwork(12) 排在 submit(13) /
       review(14) 之前，按旧 order 找下游会漏掉屏幕上显示为本阶段下游的 paperwork，
       弹窗提示与实际回退范围对不上。 */
    const doneDown = doneDownstreamOf(shownStages, stageTargets(s).map((x) => x.key),
                                      (meta.stageTemplate || []).map((x) => x.key));
    let cascade = false;
    if (doneDown.length) {
      const list = doneDown.slice(0, 8).map((x) => `· ${T(x.name)}${x.actualFinish ? `（${fmtD(x.actualFinish)} ${T("已完成")}）` : ""}`).join("\n");
      const more = doneDown.length > 8 ? `\n… ${T("另有 {0} 个", [doneDown.length - 8])}` : "";
      /* 用「确定 = 一起退回已完成的下游」而不是「确定 = 连带未完成的」：
         后者是原来那句，用户在 ADU-0059 上的困惑正是它 —— 勾了确定之后中间还留着一个「已完成」。
         现在默认口径反过来：要一次退干净，就点确定（名单已在上面列清）。 */
      cascade = window.confirm(T("这个阶段之后还有 {0} 个阶段已经完成了：\n\n{1}{2}\n\n要一起退回「未开始」吗？\n\n确定 = 连同这些已完成阶段一起退（整条流程从本阶段重来）\n取消 = 只退本阶段，这些已完成阶段保持不动（矩阵里会看到中间夹着「已完成」）", [doneDown.length, list, more])) ? "all" : false;
    } else {
      cascade = window.confirm(T("是否连同本阶段之后、尚未完成的阶段一起退回「未开始」？\n\n确定 = 一起退回；取消 = 只回退本阶段，下游保持不动。")) ? true : false;
    }
    let done = 0;
    for (const t of targets) {
      try {
        await api.post(`/design/stages/${t.id}/reopen`, { reason, cascade });
        done += 1;
      } catch (e) {
        const det = e?.response?.data?.detailRaw ?? e?.response?.data?.detail;
        toast.error(typeof det === "string" ? det : (det?.message || e.message));
        if (done) { toast.message(T("已回退 {0} 个阶段，剩余阶段未处理", [done])); reloadAll(); }
        return;
      }
    }
    toast.success(s.merged
      ? T("节点已回退（{0} 个阶段），可以从本阶段重新开始", [done])
      : (cascade === "all" && doneDown.length
        ? T("阶段已回退，{0} 个下游阶段也一起退回未开始", [doneDown.length])
        : T("阶段已回退，可以从这里重新开始")));
    reloadAll();
  };
  return (
    <div className="space-y-2" data-testid="dv3-stages">
      <NodeCheckBar nodes={nodes} nameOf={nameOf} onJump={openStage} />
      <StageStepper stages={groups} nmap={nmap} cardContents={cardContents} currentStage={currentStage} sel={sel} onSelect={(k) => { if (sel === k) setSel(null); else openStage(k); }} nameOf={nameOf} />
      <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap" data-testid="dv3-approval-mode" data-mode={mode}>
        {/* 【2026-10-20】用户两次反馈这个审批模式切换按钮不显眼：第一次「稍微明显一点」，改成实心 indigo/teal 后
            仍说「再显眼一些」。根因是它和右侧灰色小字（「15 阶段任务流 · 交付物 / 机构 / 付款…」）同处一行、
            同处一个 text-xs 视觉层级，即使有底色也被当成一行说明。
            这次进一步拉开层级：① 字号提到 14px、内边距 px-4/py-2、图标 h-4；② 渐变实心 + 外发光描边环
            （ring-2 + shadow-lg 彩色投影）让它在浅灰底上「浮」起来；③ 前置「审批模式」小标签，让人不用猜
            这颗胶囊是干什么的；④ hover 加深并抬投影、按下缩放，明确可点。 */}
        <button type="button" data-testid="dv3-approval-mode-toggle" disabled={!meta.canEdit} onClick={() => setMode(mode === "concurrent" ? "sequential" : "concurrent")} aria-pressed={mode === "concurrent"}
          title={mode === "concurrent" ? T("当前：Planning + Building 同步提交、同步审查（对应阶段合并为青色节点，{0} 个节点；当前显示 {1} 个阶段，子任务不变）· 点击切换为「分开提交」", [groups.length, shownStages.length]) : T("当前：先提交 Planning、批准后再提交 Building（当前显示 {0} 个阶段）· 点击切换为「同步提交」合并显示", [groups.length])}
          /* 同步模式末端色用 teal-700 而非 teal-600：白字压 teal-600 对比度只有约 3.7:1，低于 WCAG AA 的
             4.5:1；teal-700 约 5.5:1，同样醒目但不糊字。渐变另一端的 emerald-600 也按同口径压到 -700。 */
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 text-sm font-bold text-white shadow-lg transition-all hover:-translate-y-px hover:shadow-xl active:translate-y-0 active:scale-95 disabled:cursor-default disabled:opacity-70 disabled:active:scale-100 disabled:hover:translate-y-0 ${mode === "concurrent" ? "border-teal-700 bg-gradient-to-r from-teal-700 to-emerald-700 ring-2 ring-teal-200 shadow-teal-500/30 hover:from-teal-800 hover:to-emerald-800 hover:shadow-teal-500/40" : "border-indigo-700 bg-gradient-to-r from-indigo-600 to-violet-600 ring-2 ring-indigo-200 shadow-indigo-500/30 hover:from-indigo-700 hover:to-violet-700 hover:shadow-indigo-500/40"}`}>
          <ArrowDownUp className="h-4 w-4 shrink-0" />
          <span className="text-[11px] font-semibold opacity-80">{T("审批模式")}</span>
          {mode === "concurrent" ? T("P+B 同步提交") : T("P→B 分开提交")}
        </button>
        <select data-testid="dv3-approval-mode-select" value={mode} onChange={(e) => setMode(e.target.value)} disabled={!meta.canEdit} className="sr-only" aria-hidden="true" tabIndex={-1}>
          <option value="sequential">sequential</option><option value="concurrent">concurrent</option>
        </select>
        {/* 结构 / T24 的历史阶段已合入 Building 图，提示数量应与可见节点和总体进度一致。 */}
        <span data-testid="dv3-approval-mode-hint">{mode === "concurrent" ? T("{0} 阶段 · 对应阶段合并显示（共 {1} 个节点），底层阶段与子任务不变", [shownStages.length, groups.length]) : T("{0} 阶段任务流 · 交付物 / 机构 / 付款已按阶段挂载", [groups.length])}</span>
        <span className="ml-auto">{T("总体")} <b className="text-slate-800" data-testid="dv3-stages-overall">{formatProgress(d.overall)}</b> {T("· 内部就绪")} <b className="text-slate-800">{d.internalReadiness}%</b> {T("· 外部审批")} <b className="text-slate-800">{d.externalApproval}%</b></span>
        <button data-testid="dv3-stages-gantt" onClick={() => setGantt(!gantt)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-violet-700 font-semibold inline-flex items-center gap-1"><GanttChartSquare className="h-3.5 w-3.5" />{T("甘特图")}</button>
      </div>
      {/* 「指派」显性提示（2026-10-17 用户反馈「详情里以前有指派，现在看不见了」）：
          指派入口（阶段主负责 / 子任务负责人 / 批量指派未指派）原先只藏在**展开的阶段**里，
          默认折叠时完全看不到。这里把「有多少子任务还没指派」直接顶到阶段列表上方，一眼可见、可点。 */}
      {(() => { const un = shownStages.flatMap((s) => visibleTasks(s.key, s.tasks)).filter((t) => !t.primaryOwner && !["Completed", "Approved", "Not Applicable"].includes(t.status)).length; return un > 0 ? <p className="text-[11px] text-red-600 font-semibold inline-flex items-center gap-1" data-testid="dv3-stages-unassigned-hint"><UserPlus className="h-3.5 w-3.5" />{T("有 {0} 个子任务未指派负责人 —— 展开对应阶段即可指派（主管可「批量指派未指派」）", [un])}</p> : null; })()}
      {gantt && <div className="bg-white rounded-lg border border-slate-200 p-3 overflow-x-auto" data-testid="dv3-stages-gantt-box"><Gantt stages={shownStages} api={api} isManager={meta.isManager} onChanged={reloadAll} /></div>}
      {!sel && <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/40 px-4 py-7 text-center" data-testid="dv3-stages-empty">
        <Layers className="mx-auto h-5 w-5 text-slate-300" />
        <p className="mt-2 text-[11px] text-slate-500">{T("点上方任一阶段节点，查看该阶段的子任务、交付物与机构对接")}</p>
      </div>}
      {groups.map((g) => {
        const keys = g.merged ? g.stages.map((m) => m.key) : [g.stages[0].key];
        // 同步提交模式下 stepper 传下来的是**合并节点 key**（pb_xxx），单阶段模式传的是阶段 key
        if (!sel || (sel !== g.key && !keys.includes(sel))) return null;
        /* P+B 审图节点（pb_review）的整改块提到**节点层**只渲染一次（2026-10-22）。
           用户口径：「p+b 模式他们就是一个图了..改的话就全改」「应该像现在详情切换成
           p+b 模式的显示样式了」—— City 退回的是同一套图，整改轮次 / 结论附件
           / 通过判定都只有一份，不能再在两个阶段卡里各画一块。
           后端 correction_list 已按组聚合（返回 merged / stageKeys / passedByStage），
           这里只负责把它挂在合并节点头部、并阻止阶段卡内部重复渲染（见 renderStage）。 */
        const reviewGroup = g.merged && g.stages.some((m) => CITY_REVIEW_STAGES.includes(m.key));
        return g.merged ? (
          /* 外层只做「青色合并框 + City 整改块」；testid 用 -merged- 前缀，
             避免与下面 renderStage 渲染的阶段卡（dv3-stage-pb_set）重名 ——
             同一个 id 出现两次会让 openStage 的 scrollIntoView 与 E2E 选择器都指向外层空壳。 */
          <div key={g.key} className="rounded-lg border-2 border-teal-200 bg-teal-50/20 p-1.5 space-y-1.5" data-testid={`dv3-stage-merged-${g.key}`} data-merged={g.stages.length}>
            {/* 【2026-10-22 P+B 合并·第四轮】合并节点只渲染**一张**阶段体，不再逐成员各画一张。
                用户原话：「右侧的那些附件的上传那些也需要合到一个里面」「这些是完成了的吧？」
                —— 实测确认改造前未完成：P+B 节点里 Planning Submission Set / Building
                Architectural Set / Structural + Title 24 三张卡各有一套「节点截止」、
                负责人、任务卡、交付物区和上传按钮（dv3-stage-discs-<成员> × 3），
                同一套图被要求分三处交，与「一个图、改就全改」相反。

                做法：把成员阶段压成一个**聚合阶段**（key = 合并节点 key），
                交给同一个 renderStage 渲染一次。聚合内容：
                  · tasks      = 全部成员的子任务（上传 / 版本 / 审核入口各一个，都在这里）
                  · disciplines= groupNodes 已把成员产物并到 pb_xxx 节点上（见 groupNodes）
                  · agencies   = 同上，按 id 去重
                  · dueDate    = 最晚的一个（节点整体什么时候要交）
                  · status     = 全完成才算完成，任一 Blocked / 整改则优先体现
                这样「完成该节点」也只有一个入口（按组顺序逐个关阶段，见 closeStage）。
                sequential 项目不走这条分支（g.merged 为 false），行为完全不变。

                外层这个 div 只负责「青色边框 + City 整改块」两件事：
                节点标题 / 状态 / 完成度 / 负责人 / 完成按钮全部由下面 renderStage
                渲染一次（改造前外层还另画了一套头，导致同名 testid 与状态徽章重复）。 */}
            {/* City 审图整改：合并模式下只在节点层出现一次，覆盖组内全部审图阶段。
                代表阶段取组内第一个审图阶段（planning_review），后端据此再解析整组。 */}
            {reviewGroup && (
              <div className="px-0.5 pt-0.5" data-testid={`dv3-stage-merged-cityreview-${g.key}`}>
                <CityReviewBlock pid={pid} stageKey={g.stages.find((m) => CITY_REVIEW_STAGES.includes(m.key))?.key}
                  meta={meta} api={api} onChanged={reloadAll} />
              </div>
            )}
            {renderStage(cardContents[g.key].stage)}
          </div>
        ) : renderStage(cardContents[g.key].stage);
      })}
      {freshFiles && <TaskFilesModal task={freshFiles} api={api} zh isMgr={meta.isManager} canEdit={!!meta.canEdit} onClose={() => setFilesTask(null)} onChanged={reloadAll} />}
      {freshQa && <QAModal task={freshQa} api={api} zh lang={lang} onClose={() => setQaTask(null)} onChanged={reloadAll} />}
      {handoffAsk && <HandoffDialog address={p?.address} buildingApproved={(p?.disciplines || []).find((x) => x.key === "building")?.status === "approved"} yellowCard={p?.yellowCard || []} projectId={pid} api={api} onClose={() => setHandoffAsk(false)} onDone={() => { setHandoffAsk(false); reloadAll(); refresh?.(); }} />}
      {memberToClose && <DesignConfirmDialog testId="dv3-member-close-confirm"
        title={T("确认完成子阶段")} subtitle={T(memberToClose.name)}
        message={T("系统会检查这个子阶段的流程顺序、必需文件和产物审核。")}
        warning={T("完成后，该子阶段内尚未完成的任务会一并标记为已完成。")}
        confirmText={T("完成子阶段")} onCancel={() => setMemberToClose(null)}
        onConfirm={() => { const member = memberToClose; setMemberToClose(null); runCloseStage(member); }} />}
      {taskToReconcile && <DesignConfirmDialog testId="dv3-task-reconcile"
        title={T("纠正任务状态")} subtitle={T(taskToReconcile.title)}
        message={T("该任务已有提交的文件，确认后将任务改为进行中。")}
        warning={T("此操作不会完成任务或阶段，也不会修改文件和审核记录。")}
        withReason reasonRequired reasonLabel={T("纠正原因（至少 4 个字）")}
        confirmText={T("确认纠正")} busy={taskReconcileBusy}
        onCancel={() => setTaskToReconcile(null)} onConfirm={reconcileTaskProgress} />}
      {stageOverride && <DesignConfirmDialog testId="dv3-stage-close-override"
        title={T("强行完成阶段")} subtitle={T(stageOverride.stage.name)}
        message={<span className="whitespace-pre-line">{stageOverride.message}</span>}
        warning={T("继续会跳过以上检查，并将该阶段未完成的任务标记为已完成；原因会记录在阶段中。")
          + (["building_approval", "permit_handoff"].includes(stageOverride.stage.key) ? T("关闭该阶段可能触发第三笔款催收。") : "")}
        withReason reasonRequired reasonLabel={T("强行完成原因（至少 4 个字）")}
        confirmText={T("强行完成")} onCancel={() => settleStageOverride(null)}
        onConfirm={(reason) => {
          if (reason.length < 4) { toast.error(T("必须填写原因（至少 4 个字）")); return; }
          settleStageOverride(reason);
        }} />}
    </div>
  );
  /* 把合并节点的多个成员阶段压成**一个**阶段体（2026-10-22 P+B 合并）。
     返回对象刻意保持与普通阶段同形，好让 renderStage / LinkedDeliverables /
     任务卡 / 门禁判定全部沿用同一套代码，不必为 P+B 再写一份渲染分支。

     · key 用合并节点 key（pb_xxx）：产物 / 机构 / 任务 / 上传入口在节点里各只有一处，
       对应 testid 变成 dv3-stage-discs-pb_set、dv3-stage-tasks-pb_set 这种。
       旧脚本按成员阶段找（dv3-stage-discs-planning_set）会找不到 —— 这正是本轮需求的
       目的（三套合成一套），所以旧锚点不保留。
     · id 取**第一个成员的 id**（真实阶段 id）。它只作为兜底：节点级动作
       （完成节点 / 改截止日 / 批量指派 / 改主负责）都按 stages 逐个真实阶段下发，
       绝不能拿 "pb_set" 这种节点 key 当阶段 id 去请求接口（会 404）。 */
  function mergeStage(g) {
    const members = g.stages;
    return {
      ...g,
      id: members[0]?.id || g.id,
      key: g.key,
      name: g.name,
      status: stageStatusOfMembers(members),
      /* 子任务全并进来：任务卡自带上传 / 版本 / 审核 / 负责人，
         合成一套后「附件上传」在节点里只有一个位置。
         过滤按**成员阶段**做（visibleTasks 只对 planning_review / building_review
         生效），不能用 pb_review 去判 —— 否则旧版 City 跟进卡会漏过滤、在审图节点里
         和 CityReviewBlock 重复出现。 */
      tasks: members.flatMap((m) => visibleTasks(m.key, m.tasks)),
      completion: Math.round(members.reduce((a, m) => a + (m.completion || 0), 0) / Math.max(1, members.length)),
      /* 节点级截止取最晚：整套图什么时候能交齐由最慢的那条链决定。 */
      dueDate: maxD(members.map((m) => m.dueDate)),
      baselineDue: maxD(members.map((m) => m.baselineDue)),
      actualFinish: maxD(members.map((m) => m.actualFinish)),
      /* 这里保留组内最小 raw order 作为顺序项目 / 数据异常时的兜底；
         P+B 项目渲染门禁时会再用 flowOrderOf 把这个 pb_xxx 节点映射到
         固定五节点层序，与后端 _stage_order_lock / stage_close_core 同口径。
         成员全都没有 order 时（数据异常 / 模板缺字段）保持 null —— 不能用
         Math.min() 的空参数结果 Infinity，那会让按钮永久置灰、流程彻底卡死。 */
      order: (() => {
        const os = members.map((m) => m.order).filter((o) => o != null);
        return os.length ? Math.min(...os) : null;
      })(),
      delayHistory: members.flatMap((m) => m.delayHistory || []).sort((a, b) => String(a.at || "").localeCompare(String(b.at || ""))),
      waitType: members.find((m) => m.waitType)?.waitType || null,
      primaryOwner: members.find((m) => m.primaryOwner)?.primaryOwner || null,
      /* 本节点还要下发的真实阶段（节点级动作逐个走它们）：
         只留未完成的 —— 已完成的阶段重复下发 close 会被后端当历史阶段处理，
         改期 / 指派也不该再动已收口的阶段。 */
      stages: members,
      merged: true,
    };
  }
  /* 节点级动作的作用对象：合并节点 = 组内全部成员阶段；普通阶段 = 它自己。
     openOnly=true 时跳过已完成成员（「完成该节点」只关还没关的，幂等可重复点）。

     注意这里必须写成 function 声明：本组件的主体在 return 之后就结束了，
     下面的 const/let 语句永远不会执行（只有 function 声明会被提升），
     写成 const 箭头函数会在调用时踩 TDZ 直接抛 ReferenceError。 */
  function stageTargets(s, openOnly) {
    return (s.merged ? s.stages : [s])
      .filter((m) => !openOnly || !isStageClosed(m));
  }
  function renderStage(s) {
        const n = nmap[s.key];
        const Icon = STAGE_ICON[s.key] || Layers;
        const overdue = (n?.overdueDays || 0) > 0;
        /* 合并节点逾期来源（2026-10-29）：合并后的「止」取组内**最晚**截止日（整套图交齐时间），
           逾期天数却是组内最严重那个成员算出来的 —— 两个聚合各自独立，于是出现
           「止 12/12（未来）却标红「逾期 1 天」」这种自相矛盾的卡片（IT 报 930 / 1090 / 340 / 364）。
           IT 的诉求原话是「明确显示触发逾期的子阶段、任务及截止日」，所以这里不只给成员短标识，
           连它**自己的截止日**一起带出来（「源自 P · 止 10/27」），读者能立刻对上
           「未来日期是本组交齐时间、红字是 Planning 在 10/27 就到期」这两件事。
           成员源优先用 s.stages（groupStages 聚合时一定带全量成员数据）；
           groupNodes 下发的 n.members 只在 concurrent 模式有，作为兜底。
           普通阶段（未合并）不需要来源标签，返回 null 让后缀自动消失。 */
        const overdueMember = (() => {
          if (!s.merged) return null;
          const pool = (s.stages?.length ? s.stages : (n?.members || []));
          const ms = pool.filter((m) => Number(m.overdueDays || 0) > 0);
          if (!ms.length) return null;
          const worst = ms.reduce((a, b) => (Number(b.overdueDays || 0) > Number(a.overdueDays || 0) ? b : a));
          return { label: stageMemberShortLabel(worst.key), due: worst.dueDate || null };
        })();
        const { agencies: ag, restAgs, shownTasks, shownDiscs, carriedDiscs, memberKeys } = cardContents[s.key];
        const allDiscs = Object.fromEntries((p?.disciplines || []).map((x) => [x.key, x]));
        /* 卡片列表与角标共用 stageCardContents；shownTasks 也供未指派提示使用。 */
        const cityReviewNode = mode === "concurrent" && memberKeys.some((k) => CITY_REVIEW_STAGES.includes(k));
        /* 显示三态（2026-10-23）：图标底色 / 头部浅色带 / 徽章共用同一个 phase，
           避免出现「徽章写已完成、图标还是灰的」这种同一张卡里两套口径。
           phase 取整条流程算好的结果（只有流程前沿那一格是「进行中」），
           不再看 currentStage 指针 —— 指针会因一次通过 / 补关历史阶段而跳站。
           overdue 优先级最高（逾期是更紧急的提示），其次才是阶段自身进度。 */
        const cardPhase = phaseOfStageKey(s.key);
        const cardDone = cardPhase === STAGE_PHASE.completed;
        return (
          <div key={s.key} className={`bg-white rounded-lg border overflow-hidden ${overdue ? "border-red-200" : "border-slate-200"}`} data-testid={`dv3-stage-${s.key}`} data-merged={s.merged ? s.stages.length : undefined} data-phase={cardPhase}>
            {/* 阶段头做成浅色带：把「阶段标题」和「里面的子任务 / 交付物卡」分成两层，
                否则白卡套白卡、只剩一堆等权重的边框。 */}
            <div className={`flex items-center gap-2 px-3 py-2 border-b ${cardDone ? "bg-emerald-50/50 border-emerald-100" : overdue ? "bg-red-50/40 border-red-100" : "bg-slate-50/70 border-slate-100"}`}>
              <span className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${cardDone ? "bg-emerald-100 text-emerald-700" : s.status === "Blocked" || overdue ? "bg-red-100 text-red-600" : cardPhase === STAGE_PHASE.in_progress ? "bg-sky-600 text-white" : "bg-white text-slate-500 border border-slate-200"}`}><Icon className="h-4 w-4" /></span>
              {/* 总进度只认已完成节点；旧的任务比例可能在阶段未完成时显示 100%，因此不再展示。 */}
              <span className="text-[13px] font-semibold text-slate-800 flex-1 truncate">{T(s.name)}</span>
              {/* 【2026-10-29 修「P+B 显示未来日期却标红逾期」】
                 合并节点的「止」取组内**最晚**成员日期（整套图交给 City 要等最慢的链），
                 而「逾期 N 天」取组内**最严重**成员 —— 两者可能来自不同成员，
                 于是出现「止 2026/12/12 · 逾期 1 天」这种自相矛盾的一行。
                 IT 报告在 930 / 1090 / 340 / 364 / 1049 五个项目上都提了同一条。

                 修法不是改聚合口径（最晚日期表达「整组交齐时间」、最严重逾期表达
                 「组内最急的一条」，两个语义都正确且都有下游依赖），而是**把来源说清楚**：
                 合并节点且确有逾期时，在「逾期 N 天」后面补「· 源自 P/B/S/T24」，
                 让读者一眼知道红字来自哪个成员，而不是以为整组都逾期。
                 单一成员阶段不显示这个后缀（它自己就是来源，加了是废话）。 */}
              <span className={`text-[10px] hidden sm:inline ${overdue ? "text-red-600 font-bold" : "text-slate-400"}`} data-testid={`dv3-stage-due-${s.key}`}
                data-overdue-from={overdueMember?.label || ""} data-overdue-from-due={overdueMember?.due || ""}
                title={overdue && overdueMember ? T("逾期来自组内成员：{0}。本节点「止」显示组内最晚截止日（整套图交齐时间），两者不是同一个日期。", [overdueMember.label]) : undefined}>
                {T("止 {0}", [d10(s.dueDate)])}
                {/* 逾期段与来源段分开翻译再拼接：① 复用已有的「 · 逾期 {0} 天」词条，
                    不必为一个后缀新增带下标模板（下标写错就会静默丢字）；
                    ② 两段各自独立走词典 / LLM 缓存，英文界面不会因拼接而错位。 */}
                {overdue && T(" · 逾期 {0} 天", [n.overdueDays])}
                {overdue && overdueMember && T("（源自 {0} · 止 {1}）", [overdueMember.label, d10(overdueMember.due)])}
              </span>
              {/* 评论按**全部成员阶段**聚合：合并节点只有一个评论入口，
                  点进去能看到组内任一阶段的评论（onStageComments 接逗号分隔多键）。 */}
              <CommentBadge keys={memberKeys} tid={`dv3-stage-comments-${s.key}`} />
              {/* 阶段状态徽章（2026-10-23 三态）：只显示未开始 / 进行中 / 已完成。
                  原来的细化态（就绪 / 已提交 / 等客户 / 需整改）不再直接显示，原因见
                  meta.js 的 stagePhasesOf 注释 —— 那些词与产物状态同名，会让人误以为
                  阶段在跟着产物跑。顺序门禁、逾期、回退仍按原始 s.status 判定，
                  徽章下方保留 waitType 说明「等谁」，不算多出来一个状态。 */}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STAGE_PHASE_BADGE[cardPhase]}`}
                data-testid={`dv3-stage-status-${s.key}`} data-status={s.status}
                data-phase={cardPhase}>
                {T(STAGE_PHASE_LABEL[cardPhase])}{s.waitType ? ` · ${TE(s.waitType, WAIT_ZH)}` : ""}
              </span>
              {/* 未指派提示（2026-10-17）：不用展开阶段就能看到「这个阶段还有 N 个子任务没指派」 */}
              {(() => { const un = shownTasks.filter((t) => !t.primaryOwner && !["Completed", "Approved", "Not Applicable"].includes(t.status)).length; return un > 0 && s.status !== "Completed" ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200" data-testid={`dv3-stage-unassigned-${s.key}`} title={T("有 {0} 个子任务未指派负责人 · 展开本阶段即可指派", [un])}>{T("未指派 {0}", [un])}</span> : null; })()}
              {s.primaryOwner && <span className="text-[10px] text-slate-400 hidden sm:inline">{nameOf(s.primaryOwner)}</span>}
              {/* 「完成阶段」按钮（2026-10-21 恢复；2026-10-22 增加流程顺序门禁）：
                  2026-10-21 按「资料齐全自动推进」的口径删过，用户随后补了一句
                  「这个每个阶段的关闭按钮还是需要，这个关闭阶段其实就是完成阶段的意思」。
                  2026-10-22 用户又反过来说「我已经将完成该阶段的按钮加回来了..
                  那之前的任务提交就完成的自动应去掉了」，于是：自动推进停用（见
                  design_module._AUTO_ADVANCE_ENABLED），推进只由这个按钮触发。
                  同一轮还要求「整个流程现在需要按整个流程推进」—— 没轮到的阶段不能先完成，
                  所以这里按 s.order 与项目 currentStage 的比较结果置灰按钮，并写明原因。
                  置灰只是提示，真正的拦截在后端 stage_close_core（stage_order_violation）。
                  只给经理（与后端 stage_close 的 override 权限同一角色集），已完成阶段不再显示。

                  【2026-10-22 P+B 合并】concurrent 项目里的两个审图阶段**不显示**这个按钮：
                  它们的完成入口是合并块上的「已通过」（一次关两个阶段，且要求 City 结论附件）。
                  留着会变成「同一件事两个入口」，而点这个按钮只关一个阶段、绕过结论附件校验。
                  其他合并节点（图纸 / 材料 / 提交 / 批准）保留按钮，点击后**按组内顺序
                  逐个关闭全部未完成成员**（见 closeStage + stageTargets），一次点完成一个节点。 */}
              {meta.isManager && s.status !== "Completed"
                && !(mode === "concurrent" && memberKeys.some((k) => CITY_REVIEW_STAGES.includes(k))) && (() => {
                /* 顺序门禁从 d.stages 取原始数据，再投影成有效流程层号：
                   sequential 沿用库内 order；concurrent 按五个 P+B 节点的固定层序。
                   旧 P+B 的 planning_paperwork(6) 因此不会锁住审查节点(8)。

                   【2026-10-23 基准修正：currentStage 指针 → 流程前沿】
                   用户原话「不能跳流程让阶段开始....」。上一版拿 d.project.currentStage
                   的 order 比大小，可**指针自己会跳站**：City「一次通过」连关两个审图阶段、
                   补关历史阶段、回退后重新推进，都会让指针跑在实际进度前面。开发库实测
                   107 个在途项目里 11 个指针在前沿之后（planning_review 仍 Ready、指针已到
                   building_review）—— 这些项目「前面没做、后面却能点完成」。
                   现在基准换成 flowFrontierOf（第一个未收口阶段），与后端同一份口径；
                   前沿自己永远放行，所以「当前阶段能操作」不受影响。

                   【2026-10-24 与显示同源】顺序项目也把阶段 key 投影到「当前模板层号」
                   再比较（flowFrontierOf / flowOrderOf 都传 meta.stageTemplate）：
                   存量项目库内 order 是建项当天的旧顺序（Building 材料在提交前），
                   2026-10-23 模板已把材料挪到审查后 —— 不投影的话，按钮会按旧 order
                   被一个排在后面的格子锁死，与上面矩阵显示的顺序自相矛盾
                   （线上 3236 Rockhold 实测，影响 140 个存量项目）。 */
                const tooEarly = isTooEarlyToClose(s);
                return (
                  <Button size="sm" variant="outline" className="h-6 text-[10px]" disabled={tooEarly || stageCloseBusy}
                    data-testid={`dv3-stage-close-${s.key}`} data-too-early={tooEarly ? "1" : "0"}
                    title={tooEarly
                      ? T("流程还没走到本阶段：请先完成「{0}」", [T(flowFrontier?.name || "")])
                      : (s.merged
                        ? T("把本节点内的 {0} 个阶段一起标记为已完成并激活下一节点", [stageTargets(s, true).length])
                        : T("把本阶段标记为已完成并激活下一阶段（等同原「关闭阶段」）"))}
                    onClick={(e) => { e.stopPropagation(); if (!tooEarly) runCloseStage(s); }}>
                    <CheckCircle2 className="h-3 w-3 mr-0.5" />{s.concurrent ? T("完成该节点") : T("完成该阶段")}
                  </Button>
                );
              })()}
              {/* 「回退阶段」入口（2026-10-23 用户需求）。
                  需求原话：用户发来两个项目（ADU-0119 / ADU-0118）的矩阵截图，红框圈在
                  「方案设计」格（已完成），说「这个项目需要回退到方案设计阶段，505 也是」。
                  排查发现系统只有项目级「撤销结项」，阶段一旦完成就只能改库 —— 这里补正式入口。

                  显示条件（与后端 stage_reopen 的校验同口径，避免「按钮能点但接口 400」）：
                    · 仅设计主管 / admin / CEO（meta.isManager，与「完成该阶段」同一角色集）；
                    · 只在**已完成 / 需整改 / 已重开**的阶段上显示 —— 进行中的阶段点「回退」没有意义，
                      后端也会以 stage_not_closed 拒绝；
                    · 合并节点（P+B）同样允许：回退按组内顺序逐个退，与 closeStage 对称。
                  交互：按钮 → 弹窗（原因必填 + 是否连带下游），见 reopenStage。 */}
              {meta.isManager && (s.merged ? s.stages : [s]).some((m) => ["Completed", "Approved", "Correction Required", "Reopened"].includes(m.status)) && (
                <Button size="sm" variant="outline"
                  className="h-6 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid={`dv3-stage-reopen-${s.key}`}
                  title={T("把这个阶段退回「进行中」，用于方案返工重做（已交文件与版本历史保留）")}
                  onClick={(e) => { e.stopPropagation(); reopenStage(s); }}>
                  <RotateCcw className="h-3 w-3 mr-0.5" />{s.concurrent ? T("回退该节点") : T("回退阶段")}
                </Button>
              )}
            </div>
            <div className="px-3 pb-3 space-y-2 pt-2.5">
                {s.concurrent && s.merged && (
                  <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-teal-100 bg-teal-50/30 px-2.5 py-2"
                    data-testid={`dv3-stage-members-${s.key}`}>
                    <span className="text-[10px] font-semibold text-slate-500">{T("子阶段")}</span>
                    {s.stages.map((member) => {
                      const closed = isStageClosed(member);
                      const memberTooEarly = isTooEarlyToClose(member);
                      return (
                        <div key={member.key} className="flex flex-wrap items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1"
                          data-testid={`dv3-stage-member-${member.key}`} data-status={member.status}>
                          <span className="text-[10px] font-medium text-slate-700">{T(member.name)}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${closed ? "bg-emerald-50 text-emerald-700" : member.status === "Blocked" || member.status === "Correction Required" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                            {teStatus(member.status)}
                          </span>
                          {meta.isManager && !closed && !cityReviewNode && (
                            <Button size="sm" variant="outline" className="h-6 min-h-11 min-w-11 px-2 text-[10px] md:min-h-0 md:min-w-0"
                              data-testid={`dv3-stage-member-close-${member.key}`} disabled={memberTooEarly || stageCloseBusy}
                              title={memberTooEarly
                                ? T("流程还没走到本阶段：请先完成「{0}」", [T(flowFrontier?.name || "")])
                                : T("通过现有门禁完成此子阶段；阶段内未完成子任务会一并收口")}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (memberTooEarly) return;
                                setMemberToClose(member);
                              }}>
                              <CheckCircle2 className="h-3 w-3 mr-0.5" />{T("完成")}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                    {isTooEarlyToClose(s) && <span className="basis-full text-xs text-amber-800"
                      data-testid={`dv3-stage-order-hint-${s.key}`}>
                      {T("暂时无法完成此节点，请先完成「{0}」。", [T(flowFrontier?.name || "")])}
                    </span>}
                  </div>
                )}
                {/* FB5999a8a85a：沿用设置页服务端 canEdit，移交阶段可直接维护同一编号。 */}
                {s.key === "permit_handoff" && <HandoffPermitField project={p || d.project} canEdit={!!meta.canEdit} api={api} onSaved={reloadAll} />}
                <div className="flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
                  <CalendarClock className="h-3.5 w-3.5" /><span>{T("节点截止")} <b className={overdue ? "text-red-600" : "text-slate-800"}>{d10(s.dueDate)}</b>{s.baselineDue && d10(s.baselineDue) !== d10(s.dueDate) ? <span className="text-slate-400"> {T("· 基线 {0}", [d10(s.baselineDue)])}</span> : ""}{(s.delayHistory || []).length ? <span className="text-amber-700"> {T("· 改期 {0} 次（{1}）", [s.delayHistory.length, s.delayHistory[s.delayHistory.length - 1].reason])}</span> : ""}</span>
                  {meta.isManager && s.status !== "Completed" && <button type="button" data-testid={`dv3-stage-resched-${s.key}`} onClick={() => rescheduleStage(s)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-violet-700 font-semibold hover:underline">{T("改截止日")}</button>}
                  {meta.canEdit && <span className="inline-flex items-center gap-1 ml-auto">{T("阶段主负责")}{(() => {
                    const opts = ownerOpts(s.primaryOwner);
                    if (!opts.length) return <span className="text-[10px] text-amber-600" data-testid={`dv3-stage-owner-empty-${s.key}`}>{ownerHint}</span>;
                    /* 【2026-10-22 P+B 合并】节点主负责 = 组内全部成员阶段一起改。
                       节点只显示一个负责人（取第一个有值的成员），只改一半会让
                       同一套图在两条链上挂两个不同负责人，之后谁该交图就说不清了。
                       已完成的阶段不动：历史责任人不能被事后改写。 */
                    return <select data-testid={`dv3-stage-owner-${s.key}`} value={s.primaryOwner || ""} onChange={async (e) => {
                      const who = e.target.value || null;
                      try {
                        for (const t of stageTargets(s, true)) await api.patch(`/design/stages/${t.id}`, { primaryOwner: who });
                        reloadAll();
                      } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
                    }} className="border border-slate-200 rounded px-1.5 py-0.5 text-[11px]"><option value="">{T("未指派")}</option>{opts.map((u) => <option key={u.username} value={u.username}>{ownerOptionLabel(u)}</option>)}</select>;
                  })()}</span>}
                  {meta.isManager && (() => { const un = shownTasks.filter((t) => !t.primaryOwner && !["Completed", "Approved", "Not Applicable"].includes(t.status)).length; const opts = ownerOpts(""); return (
                    <span className="inline-flex items-center gap-1" data-testid={`dv3-stage-bulk-${s.key}`}><UserPlus className="h-3.5 w-3.5 text-violet-500" />
                      {opts.length ? (
                        <select data-testid={`dv3-stage-bulk-owner-${s.key}`} value={bulk[s.key] || ""} onChange={(e) => setBulk({ ...bulk, [s.key]: e.target.value })} className="border border-slate-200 rounded px-1.5 py-0.5 text-[11px]"><option value="">{T("选择负责人…")}</option>{opts.map((u) => <option key={u.username} value={u.username}>{ownerOptionLabel(u)}</option>)}</select>
                      ) : <span className="text-[10px] text-amber-600" data-testid={`dv3-stage-bulk-owner-empty-${s.key}`}>{ownerHint}</span>}
                      <Button size="sm" variant="outline" className="h-6 text-[10px]" data-testid={`dv3-stage-bulk-assign-${s.key}`} disabled={!un || !bulk[s.key]} onClick={() => bulkAssign(s)}>{T("批量指派未指派（{0}）", [un])}</Button></span>); })()}
                </div>
                {/* 「本阶段无子任务」提示按去重后的口径判断（2026-10-21）：只可能出现在
                    「本阶段任务全部是旧版 City 跟进卡」的情况（那类卡已由 CityReviewBlock 接管）。
                    合并节点的 tasks 在 mergeStage 里已按成员阶段过滤过，直接用 s.tasks 判空。 */}
                {(s.tasks || []).length > 0 && shownTasks.length === 0
                  && <p className="text-[11px] text-slate-400" data-testid={`dv3-stage-tasks-absorbed-${s.key}`}>{T("本阶段进度由 City Review 区块跟进（见下方审图整改）")}</p>}
                {/* 子任务也走同一套「按容器宽度算列数」：整行通栏时，一张卡横跨 1300px，
                    标题在左、状态下拉在右，中间空出一大片，扫读成本反而更高。 */}
                {(() => {
                  /* Land Survey 可选块改挂到任务网格里（2026-10-21 用户需求）：
                     用户画了箭头，要求把它从「交付物」下方挪到任务卡**右侧**的空位 ——
                     理由是「这个需要交付的话需要交付资料」，它本身就是一项交付，
                     不该排在交付物区之后、离任务卡那么远。
                     实现上不再另起一行，而是作为任务网格的**同行第二个格子**：
                     任务卡通常只占第 1 列（方案设计阶段只有 1 张任务卡），右侧整片空着；
                     块宽用 span(tCols-1) 吃掉这一行剩下的列，窄屏（tCols=1）自然落到下一行整宽。
                     注意：即使本阶段没有任务卡也要渲染它（否则可选块会凭空消失），
                     所以判空条件同时看 shownTasks 与 LAND_SURVEY_STAGES。
                     等高（2026-10-21 用户需求「左右卡片高度对齐」）：有 Land Survey 时用
                     items-stretch 把同一行的两格拉等高 —— 任务卡往往只有三四行，Land Survey
                     有提问 + 两个按钮 + 说明，按 items-start 排会左矮右高、底边不齐。
                     没有 Land Survey 的阶段仍用 items-start：多张任务卡各自按内容成高，
                     强行等高会把内容少的卡拉出大片空白。 */
                  const withLandSurvey = LAND_SURVEY_STAGES.includes(s.key);
                  if (!shownTasks.length && !withLandSurvey) return null;
                  const lsSpan = Math.max(1, tCols - 1);
                  return (
                <div ref={bindTaskGrid} className={`grid gap-2 ${withLandSurvey ? "items-stretch" : "items-start"}`} style={{ gridTemplateColumns: `repeat(${tCols}, minmax(0, 1fr))` }} data-testid={`dv3-stage-tasks-${s.key}`}>
                {/* 渲染时跳过已被 CityReviewBlock 取代的旧版「City 状态跟进」卡（见 visibleTasks 注释）。
                    数据仍保留在库里，只是不再出现在这个阶段卡片里。
                    配对任务（Site Plan + Floor Plan / Planning 全套 / Building Set / Structural /
                    Title 24）**照常成卡** —— 2026-10-21 用户明确「保留上面那种…之前卡片本来就有
                    这个功能啊」，它们本来就承载上传 / 文件版本 / QA / 改期 / 负责人，是用户熟悉的主入口；
                    重复的是产物侧那张只有状态和文件数的卡，改为在 LinkedDeliverables 里收起。 */}
                {shownTasks.map((t) => {
                  const gate = meta.canEdit && (t.requiredFileTypes || []).length > 0;
                  const missing = (t.requiredFileTypes || []).filter((r) => !(t.files || []).some((f) => f.type === r));
                  /* 【2026-10-29 逾期口径对齐后端】原来是 `new Date(t.dueDate) < new Date()` ——
                     拿瞬时比瞬时，两个问题：
                     ① dueDate 落库是洛杉矶零点瞬间（UTC 07:00/08:00），只要「现在」过了
                        洛杉矶零点（洛杉矶上午 9 点 = UTC 17 点），当天到期的任务立刻被判逾期，
                        而到期当天按后端口径不该算逾期；
                     ② 浏览器时区不同，同一张卡在中国同事那里会提前一天变红。
                     后端 design_v3._due_days 的口径是「洛杉矶日历日；到期当天 0 天、>0 才算逾期」，
                     这里换成同一套：先归一到洛杉矶日历日，再和「洛杉矶今天」比大小。 */
                  const tOver = !["Completed", "Approved", "Not Applicable", "Not Started"].includes(t.status)
                    && !!t.dueDate && (dateInputValue(t.dueDate) || "") < usToday();
                  /* 该任务是否对应一项 v3 产物（配对关系）：用于在任务卡上补「产物图纸」入口。
                     为什么需要：产物卡收起后，产物侧上传的图纸版本与送审记录（设计师在产物里传过图、
                     走过审核，dev 库实测 90 个配对产物有 162 份文件）就只剩这个入口能到。
                     入口只在产物侧确实有内容时出现，避免每个任务卡都挂一个空按钮。 */
                  const pairKey = TASK_TO_DISC[t.title];
                  const pairDisc = pairKey ? allDiscs[pairKey] : null;
                  const pairFileN = pairDisc ? (pairDisc.files || []).filter((f) => !f.deleted && !f.voided).length : 0;
                  const pairRev = pairKey ? (p?.reviews || []).find((x) => x.disciplineKey === pairKey) : null;
                  const ownerStage = (d.stages || []).find((stage) => stage.key === t.stageKey);
                  const canReconcile = meta.isManager && meta.canEdit && !isClosedProject(p) && !!pairKey
                    && ownerStage && !isStageClosedStatus(ownerStage.status) && !ownerStage.actualFinish
                    && ["Not Started", "Ready"].includes(t.status)
                    && (t.files || []).some((f) => ["Submitted", "Approved"].includes(f.status) && f.fileId && !f.deleted && !f.voided);
                  return (
                  <div key={t.id} onDoubleClick={(e) => { if (e.target.closest("select,button,label,input,a")) return; setFilesTask(t); }} title={T("双击打开文件与版本")}
                    /* flex-col（2026-10-21 等高）：与 Land Survey 并排时这一格会被拉伸到
                       与右侧同高，卡片内部改成纵向 flex，让末尾的操作条用 mt-auto 贴住
                       底边，而不是内容整体居中或按钮悬在半空 —— 左右两卡的操作区对齐，
                       扫读时视线不用上下找。 */
                    className={`flex flex-col rounded-lg border p-3 transition-colors cursor-default ${TASK_TONE(t.status, tOver).card} ${dropping === t.id ? "border-violet-400 ring-2 ring-violet-200/70 bg-violet-50/60" : ""}`} data-testid={`dv3-ltask-${t.id}`}
                    {...(gate ? { onDragOver: (e) => { e.preventDefault(); if (dropping !== t.id) setDropping(t.id); }, onDragLeave: () => setDropping(null), onDrop: (e) => { e.preventDefault(); setDropping(null); dropFiles(t, e.dataTransfer.files); } } : {})}>
                    {/* 长方形卡片：状态点 + 标题 → 元信息 → 操作条 */}
                    <div className="flex items-start gap-2">
                      <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${TASK_TONE(t.status, tOver).dot}`} />
                      <span className="text-[13px] leading-5 font-semibold text-slate-800 flex-1 min-w-0">{t.isGate && <FileCheck2 className="h-3.5 w-3.5 inline text-indigo-500 mr-1" />}{T(t.title)}</span>
                      {/* 任务状态下拉已移除（2026-10-21 用户需求）：
                          用户明确「这个选项不需要了，因为现在都是按照阶段推荐，都是这些可以选择的项
                          都是必传才能进行下一个步骤」—— 任务状态不再由人任选，而是由「文件传没传」
                          决定：传齐必需文件即推进（后端 maybe_auto_advance_stage），
                          产物审核通过时配对任务同步收口（design_v3._close_paired_stage_tasks）。
                          保留只读状态标签：用户仍需要一眼看到这一步走到哪了，只是不再给手选入口，
                          避免「没传文件也能点成已完成」把门禁绕过。 */}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-md shrink-0 ${STATUS_BADGE(t.status)}`} data-testid={`dv3-ltask-status-${t.id}`} data-status={t.status}>{teStatus(t.status)}</span>
                    </div>
                    <p className="mt-1.5 text-[11px] leading-5 text-slate-500">{TE(t.primaryRole, ROLE_ZH)} · <span className={tOver ? "text-red-600 font-semibold" : ""}>{T("截止")} {d10(t.dueDate)}</span>{t.requiredFileTypes?.length ? T(" · 需 {0}", [t.requiredFileTypes.map((x) => TE(x, FILE_TYPE_ZH)).join("/")]) : ""}{(t.files || []).length ? T(" · 文件 {0}", [t.files.length]) : ""}{t.qa ? ` · QA ${t.qa.result === "pass" ? "通过" : "打回"}` : ""}</p>
                    {/* 配对产物摘要（2026-10-21）：产物卡已收进「已在上面任务卡内办理」那一行，
                        但产物侧的图纸版本 / 送审状态是另一份数据，必须在这里能一眼看到。
                        只在有配对产物时出现，一句话说清「同一份交付的产物侧走到哪了」。 */}
                    {pairDisc && (pairFileN > 0 || pairRev) && (
                      <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-500 flex-wrap" data-testid={`dv3-ltask-pairdisc-${t.id}`} data-status={pairDisc.status}>
                        <Package className="h-3 w-3 shrink-0 text-slate-400" />
                        <span className="truncate">{T("产物「{0}」", [T(pairDisc.name)])}</span>
                        <span className="rounded px-1 text-[10px] font-semibold shrink-0" style={{ color: (DS_COLOR[pairDisc.status] || {}).c || "#475569", background: (DS_COLOR[pairDisc.status] || {}).bg || "#f1f5f9" }}>{discStatusLabel(meta, pairDisc.status, pairDisc.discKind)}</span>
                        {pairFileN > 0 && <span className="tabular-nums">{T("{0} 份", [pairFileN])}</span>}
                        {pairRev?.status === "pending" && <span className="inline-flex items-center gap-0.5 text-amber-700 font-semibold"><Hourglass className="h-3 w-3" />{T("待 {0} 复核", [pairRev.reviewer || T("设计部经理")])}</span>}
                      </p>
                    )}
                    {t.blockReason && <p className="mt-1 text-[11px] text-red-500"><AlertTriangle className="h-3 w-3 inline" /> {t.blockReason}</p>}
                    {canReconcile && <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-amber-800"
                      data-testid={`dv3-ltask-reconcile-hint-${t.id}`}>
                      <span>{T("文件已提交，任务状态待纠正")}</span>
                      <Button size="sm" variant="outline" className="h-7" data-testid={`dv3-ltask-reconcile-${t.id}`}
                        onClick={() => setTaskToReconcile(t)}>{T("纠正状态")}</Button>
                    </div>}
                    {/* 配对产物的「审核通过 / 退回」入口（2026-10-21）：
                        产物卡收起后，审核人再也点不到产物侧那两个按钮，而阶段门禁认的正是
                        「产物审核通过」。所以把这两个动作搬到任务卡上 —— 用户在这里看到的
                        就是同一条流程：传图 → 送审 → 审核通过（阶段自动进入下一步）。
                        权限与后端 _can_review 同口径（主管 / 美国设计师 / 该产物配置的审核人），
                        与「谁能改任务」无关：审核人通常不是这张任务的负责人。
                        只在有待复核记录时出现，避免每张卡都挂两个按不到的按钮。 */}
                    {pairRev?.status === "pending" && (meta.isManager || meta.me?.role === "us_policy" || meta.me?.username === pairRev.reviewerUsername) && (
                      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`dv3-ltask-pairdisc-review-${t.id}`}>
                        <button type="button" disabled={!!pairBusy}
                          data-testid={`dv3-ltask-pairdisc-accept-${t.id}`}
                          onClick={() => decidePair(t, pairRev, "accepted")}
                          className="h-6 inline-flex items-center gap-0.5 rounded-md border border-emerald-300 bg-emerald-50 px-1.5 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
                          <CheckCircle2 className="h-3 w-3" />{T("审核通过")}
                        </button>
                        <button type="button" disabled={!!pairBusy}
                          data-testid={`dv3-ltask-pairdisc-reject-${t.id}`}
                          onClick={() => decidePair(t, pairRev, "needs_fix")}
                          className="h-6 inline-flex items-center gap-0.5 rounded-md border border-red-300 bg-red-50 px-1.5 text-[10px] font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50">
                          <RotateCcw className="h-3 w-3" />{T("退回重做")}
                        </button>
                        {pairBusy === t.id && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
                      </div>
                    )}
                    {gate && <p className={`mt-1 text-[11px] ${dropping === t.id ? "text-violet-700 font-semibold" : "text-slate-400"}`} data-testid={`dv3-ltask-drophint-${t.id}`}>{uploadingId === t.id ? T("上传中…") : dropping === t.id ? T("松开即上传 → {0}", [TE(inferFileType("x.pdf", t.requiredFileTypes, (t.files || []).map((f) => f.type)), FILE_TYPE_ZH)]) : missing.length ? T("拖入图纸即上传 · 缺 {0}", [missing.map((x) => TE(x, FILE_TYPE_ZH)).join("/")]) : T("拖入图纸即上传新版本")}</p>}
                    {meta.canEdit && (
                      /* mt-auto（2026-10-21 等高）：卡片被拉高时把操作条推到底边，
                         与右侧 Land Survey 的底部对齐；内容不足时行为与原来的 mt-2 一致。 */
                      <div className="mt-auto flex items-center gap-1.5 flex-wrap border-t border-slate-200/70 pt-2">
                        {(() => { const opts = ownerOpts(t.primaryOwner); return opts.length ? (
                          <select data-testid={`dv3-ltask-owner-${t.id}`} value={t.primaryOwner || ""} onChange={(e) => setTaskOwner(t, e.target.value)} className="text-[10px] border border-slate-200 rounded px-1 py-1 max-w-[100px]"><option value="">{T("未指派")}</option>{opts.map((u) => <option key={u.username} value={u.username}>{ownerOptionLabel(u)}</option>)}</select>
                        ) : <span className="text-[10px] text-amber-600" data-testid={`dv3-ltask-owner-empty-${t.id}`}>{ownerHint}</span>; })()}
                        <button data-testid={`dv3-ltask-files-${t.id}`} title={T("文件与版本")} onClick={() => setFilesTask(t)} className="relative text-slate-400 hover:text-violet-700"><FolderOpen className="h-4 w-4" />{(t.files || []).length > 0 && <span className="absolute -top-1.5 -right-1.5 bg-violet-600 text-white rounded-full text-[8px] w-3.5 h-3.5 flex items-center justify-center">{t.files.length}</span>}</button>
                        {gate && <label data-testid={`dv3-ltask-upload-${t.id}`} title={T("快速上传图纸（不打开文件弹窗）")} className="cursor-pointer text-slate-400 hover:text-violet-700">{uploadingId === t.id ? <Loader2 className="h-4 w-4 animate-spin text-violet-600" /> : <UploadCloud className="h-4 w-4" />}<input type="file" multiple className="hidden" data-testid={`dv3-ltask-upload-input-${t.id}`} accept={DESIGN_UPLOAD_ACCEPT} onChange={(e) => { dropFiles(t, Array.from(e.target.files)); e.target.value = ""; }} /></label>}
                        <button data-testid={`dv3-ltask-qa-${t.id}`} title="QA" onClick={() => setQaTask(t)} className={t.qa ? (t.qa.result === "pass" ? "text-emerald-500" : "text-red-500") : "text-slate-400 hover:text-violet-700"}><ShieldCheck className="h-4 w-4" /></button>
                        <button data-testid={`dv3-ltask-resched-${t.id}`} title={T("改期")} onClick={() => reschedule(t)} className="text-slate-400 hover:text-amber-600"><CalendarClock className="h-4 w-4" /></button>
                      </div>
                    )}
                  </div>
                  );
                })}
                {/* Land Survey 可选块放在任务网格里（2026-10-21 用户需求）。
                    位置与原因见上方 withLandSurvey 注释：用户要求挪到任务卡右侧的空位，
                    并说明「这个需要交付的话需要交付资料」—— 它本身就是一项要交报告的交付，
                    所以跟任务卡并排，不再排到「交付物」区块之后。 */}
                {withLandSurvey && (
                  /* flex（2026-10-21 等高）：本格被 grid stretch 拉高后，让 LandSurveyBlock
                     撑满整格高度，块的边框才能与左侧任务卡底边齐平 —— 否则外层是 div、
                     内层按内容高，右边框会在按钮下沿就收口，视觉上仍不齐。 */
                  <div className="flex min-w-0 flex-col" style={{ gridColumn: `span ${lsSpan}` }} data-testid={`dv3-stage-landsurvey-cell-${s.key}`}>
                    <LandSurveyBlock pid={pid} stageKey={s.key} meta={meta} api={api} onChanged={reloadAll} />
                  </div>
                )}
                </div>
                  );
                })()}
                {PRE_KEYS.includes(s.key) && <PreStagePanel stageKey={s.key} pid={pid} meta={meta} onChanged={reloadAll} />}
                {/* 交付物区与角标使用同一份已过滤的卡片清单。 */}
                {n && <LinkedDeliverables pid={pid} node={n} p={p} meta={meta} api={api} onChanged={reloadAll} agencies={ag} agencyDocs={d.agencyDocs || {}} designers={designers} stageTasks={s.tasks || []} shownDiscs={shownDiscs} carriedDiscs={carriedDiscs} agToDisc={agToDisc} onOpenLegacyFiles={setFilesTask} agencyResp={d.agencyResp || {}} />}
                {/* City 审图多轮整改（2026-10-20 用户需求）：
                    只挂在 Planning Review / Building Review 两个阶段 —— 这两个阶段才是市府
                    出 plan check 意见的地方。块内含「上一次提交是否通过」的判定按钮：
                    通过 → 关阶段进入下一步；未通过 → 新增一轮整改（图纸 + 回复函），
                    每新增一轮把项目整体顺延 3 天（天数可在「产物与流程设置 → 全流程责任」改）。
                    放在产物块之后、交图邮件之前：它承接的正是「已交图 → City 给结果」这一步。

                    【2026-10-22 P+B 合并】concurrent 项目里**不再**在阶段卡内渲染：
                    整改块已提到合并节点层只画一次（见上方 reviewGroup）。若这里继续渲染，
                    同一个 City 结论会出现两块（用户截图里就是这个现象），而且两块共用
                    同一份组轮次数据，点哪块都在改同一件事 —— 必须只留一处入口。
                    sequential 项目（approvalMode 非 concurrent）行为完全不变。 */}
                {CITY_REVIEW_STAGES.includes(s.key) && mode !== "concurrent"
                  && <CityReviewBlock pid={pid} stageKey={s.key} meta={meta} api={api} onChanged={reloadAll} />}                {restAgs.length > 0 && (() => {
                  /* 【2026-10-22 用户反馈】「这个阶段的其他机构对接是必填吗」「这些东西在流程中
                     不是必选项....他的状态的作用到底是什么?」

                     修改原因：restAgs 里此前混着两类性质完全不同的机构，都塞在同一格网格里，
                     用户无法分辨哪些是流程真要办的、哪些只是选配登记：
                       ① 有关联产物、但那份产物挂在**别的阶段**（MEP → 建筑，建筑在 Building 图阶段）：
                          仍有明确的交付归属，本单确实要出那份图；
                       ② 压根没有对应产物（泳池 / 新地址 / 其他市政）：纯选配项 ——
                          stage_close_core 从头到尾不读机构表（实测该函数内 "agency" 出现 0 次），
                          所以它们的完成与否**不影响阶段关闭**，也不会挡住自动推进。
                     最终效果：按这两类分栏。② 单独成一块并标注「可选项」，标题里写明不参与
                     阶段推进 —— 避免用户把「0/2」误读成「还差 2 项才能往下走」。
                     关键取舍：不隐藏这些机构（选配项本身要能登记、能传凭证），
                     也不改它们的计数口径，只把「必办 / 选配」的语义显性化。 */
                  const agLinkedElsewhere = restAgs.filter((a) => agToDisc[a.type]);
                  const agOptional = restAgs.filter((a) => !agToDisc[a.type]);
                  const doneOf = (list) => list.filter((a) => AG_DONE.includes(a.status) || a.applicability === "Exempt/Not Applicable").length;
                  const card = (a, optional) => {
                    const dk = agToDisc[a.type];
                    const linked = dk ? allDiscs[dk] : null;
                    const linkedDone = linked && ["approved", "done", "exempt"].includes(linked.status);
                    return (
                      <div key={a.id} className="rounded-lg border border-slate-200 bg-white p-2 space-y-1.5" data-testid={`dv3-agency-card-${a.id}`}>
                        <AgencyRow a={a} meta={meta} api={api} designers={designers} onChanged={reloadAll} compact docs={d.agencyDocs?.[a.type] || []} resp={d.agencyResp || {}} />
                        {/* 关联提示：把「这个机构等的是哪份产物」写清楚，避免被误认为还要另交一份图纸。
                            选配项整块已有「可选项 · 不参与阶段推进」的标题，卡内不再重复一句
                            「无对应产物」—— 每张卡都说一遍只会变成噪音。 */}
                        {!optional && (
                          <p className={`text-[10px] flex items-start gap-1 ${linked ? (linkedDone ? "text-emerald-700" : "text-amber-700") : "text-slate-400"}`} data-testid={`dv3-agency-linked-${a.id}`}>
                            {linked ? (linkedDone ? <CheckCircle2 className="h-3 w-3 shrink-0 mt-px" /> : <Hourglass className="h-3 w-3 shrink-0 mt-px" />) : <span className="h-3 w-3 shrink-0" />}
                            <span className="min-w-0">{linked ? <>{T("关联产物：{0} · {1}", [T(linked.name), discStatusLabel(meta, linked.status, allDiscs[linked.key]?.discKind)])}{linkedDone ? T(" · 图纸已就绪，可送审") : T(" · 图纸未完成，暂不能送审")}</> : T("无对应产物（选配项）")}</span>
                          </p>
                        )}
                      </div>
                    );
                  };
                  /* auto-fill + 300px 最小宽：卡片宽度自动和上面那排产物卡对齐，
                     数量少时右侧留空轨道，而不是把两张卡拉成整行长条、内容被推到两端。 */
                  const grid = (list, optional) => (
                    <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
                      {list.map((a) => card(a, optional))}
                    </div>
                  );
                  return (
                  <div className="space-y-2" data-testid={`dv3-stage-agencies-${s.key}`}>
                    <div className="flex items-center gap-2 px-0.5">
                      <Landmark className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span className="text-[11px] font-bold uppercase tracking-wide text-slate-600">{T("其他机构对接")}</span>
                      <span className="text-[11px] font-semibold text-slate-900 tabular-nums">{doneOf(restAgs)}/{restAgs.length}</span>
                      <span className="text-[10px] text-slate-400 truncate">{T("截止 {0}", [d10(s.dueDate)])}</span>
                      {/* 一眼说清这类机构不进阶段门禁：用户正是被「0/2」误导以为卡流程。
                          放在标题行末尾而不是卡片里，是因为它对整块都成立。 */}
                      <span className="text-[10px] text-slate-400 truncate" data-testid={`dv3-stage-agencies-hint-${s.key}`}>{T("不参与阶段推进")}</span>
                    </div>
                    {agLinkedElsewhere.length > 0 && grid(agLinkedElsewhere, false)}
                    {agOptional.length > 0 && (
                      <div className="space-y-1.5 pt-0.5" data-testid={`dv3-stage-agencies-optional-${s.key}`}>
                        {/* 分栏标题：可选项 = 没有对应产物的机构（泳池 / 新地址 / 其他市政）。
                            用户反馈的原话是「这些东西在流程中不是必选项」，所以这里直接把
                            「不参与阶段推进」写进标题，而不是让人从徽章去猜。 */}
                        <div className="flex items-center gap-1.5 px-0.5">
                          <span className="h-3 w-0.5 rounded-full bg-slate-300 shrink-0" />
                          <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{T("可选项")}</span>
                          <span className="text-[10px] font-semibold text-slate-400 tabular-nums">{doneOf(agOptional)}/{agOptional.length}</span>
                          <span className="text-[10px] text-slate-400 truncate">{T("无对应产物 · 不参与阶段推进，本单不需要时可不处理")}</span>
                        </div>
                        {grid(agOptional, true)}
                      </div>
                    )}
                  </div>
                  );
                })()}
                {/* 交图邮件（每个节点只有一个入口）。
                    【2026-10-22 P+B 合并】合并节点的机构类型取**成员阶段的并集**：
                    STAGE_AGENCY 只按单个阶段配了默认机构（planning_submit → Planning
                    Department、building_submit → Building Department），合并节点键
                    （pb_submit）在表里查不到，会退回「本项目全部机构类型」，
                    等于把 Fire/School Fee 这些也塞进交图收件人候选。
                    后端 _compose / _stage_files 已按合并节点展开成组内全部阶段，
                    这里只要把机构类型传对即可。 */}
                {(() => {
                  const types = Array.from(new Set(memberKeys.flatMap((k) => STAGE_AGENCY[k] || [])));
                  const fallback = types.length ? types : ag.map((a) => a.type);
                  return (types.length || ag.length > 0) && (
                    <SubmittalPanel pid={pid} stageKey={s.key} agencyTypes={fallback} meta={meta} onChanged={reloadAll} compact />
                  );
                })()}
              </div>
          </div>
        );
  }
};

const SERVICE_TYPES = [["design_build", "设计 + 施工（17 阶段全链 · 23 项子任务）"], ["design_only", "仅设计（8 阶段）"], ["permit_only", "仅报批（6 阶段）"]];

const InstantiatePanel = ({ pid, meta, api, project, onDone }) => {
  const [st, setSt] = useState(project?.serviceType || "design_build");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/design/projects/${pid}/instantiate`, { serviceType: st }); toast.success(T("已补建 {0} 个阶段 · {1} 项子任务{2}", [data.stages, data.tasks, data.completedStages ? T("（前 {0} 个阶段按当前进度标记完成）", [data.completedStages]) : ""])); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <div className="bg-white rounded-lg border border-dashed border-slate-300 p-6 text-center space-y-3" data-testid="dv3-stages-empty">
      <Layers className="h-8 w-8 text-slate-300 mx-auto" />
      <p className="text-sm font-semibold text-slate-700">{T("该项目尚未生成阶段任务链")}</p>
      <p className="text-xs text-slate-500">{T("补建后将按模板生成阶段与子任务（文件门禁 / QA / 改期），并依据当前所处阶段自动把之前的阶段标记为已完成；同时补齐机构清单与设计费里程碑（如缺）。")}</p>
      {meta.isManager ? (
        <div className="flex flex-wrap items-center justify-center gap-2">
          <select data-testid="dv3-instantiate-type" value={st} onChange={(e) => setSt(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5">{SERVICE_TYPES.map(([k, l]) => <option key={k} value={k}>{T(l)}</option>)}</select>
          <Button size="sm" data-testid="dv3-instantiate-btn" onClick={run} disabled={busy} className="bg-slate-900 hover:bg-slate-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Layers className="h-4 w-4 mr-1" />{T("一键补建阶段任务链")}</>}</Button>
        </div>
      ) : <p className="text-[11px] text-slate-400" data-testid="dv3-instantiate-nomgr">{T("请联系设计经理补建")}</p>}
    </div>
  );
};

const Gantt = ({ stages, api, isManager, onChanged }) => {
  const [drag, setDrag] = useState(null);
  const trackRef = useRef(null);
  const all = stages.flatMap((s) => [s.baselineStart, s.baselineEnd, s.forecastStart, s.forecastEnd, s.actualStart, s.actualFinish, s.plannedStart, s.dueDate].filter(Boolean).map((x) => new Date(x).getTime()));
  if (!all.length) return <p className="text-xs text-slate-400">{T("无基线数据")}</p>;
  const min = Math.min(...all), max = Math.max(...all) + 86400000 * 3, span = Math.max(1, max - min);
  const pos = (a, b) => ({ left: `${((new Date(a).getTime() - min) / span) * 100}%`, width: `${Math.max(1, ((new Date(b).getTime() - new Date(a).getTime()) / span) * 100)}%` });
  const cascade = async (s, deltaDays) => {
    if (!deltaDays) return;
    const reason = window.prompt(T("自「{0}」起及其后所有未完成阶段{1} {2} 天，请填写原因（必填，保留基线）", [T(s.name), deltaDays > 0 ? T("顺延") : T("提前"), Math.abs(deltaDays)]));
    if (!reason) return toast.error(T("必须填写原因，已取消"));
    try { const { data } = await api.post(`/design/stages/${s.id}/reschedule-cascade`, { deltaDays, reason }); toast.success(T("已{0} {1} 天：{2} 个阶段 · {3} 项子任务同步", [deltaDays > 0 ? T("顺延") : T("提前"), Math.abs(deltaDays), data.shiftedStages, data.shiftedTasks])); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const shiftAll = () => {
    const first = stages.find((s) => s.status !== "Completed") || stages[0];
    const v = window.prompt(T("整体顺延天数（负数为提前）· 自「{0}」起", [T(first.name)]), "7"); if (v === null) return;
    const n = parseInt(v, 10); if (Number.isNaN(n) || n === 0) return toast.error(T("请输入非 0 整数天数"));
    cascade(first, n);
  };
  const pxPerDay = () => (trackRef.current ? trackRef.current.getBoundingClientRect().width / (span / 86400000) : 0);
  const onDown = (e, s) => { if (!isManager || s.status === "Completed") return; e.preventDefault(); setDrag({ id: s.id, x0: e.clientX, dx: 0 }); };
  const onMove = (e) => { if (drag) setDrag({ ...drag, dx: e.clientX - drag.x0 }); };
  const onUp = () => { if (!drag) return; const s = stages.find((x) => x.id === drag.id); const days = Math.round(drag.dx / (pxPerDay() || 1)); setDrag(null); if (s && days) cascade(s, days); };
  const dayShift = drag ? Math.round(drag.dx / (pxPerDay() || 1)) : 0;
  return (
    <div className="min-w-[560px] space-y-1 select-none" data-testid="dv3-gantt" onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}>
      {isManager && <div className="flex-wrap md:flex-nowrap flex items-center gap-2 text-[10px] text-slate-500 mb-1"><MoveHorizontal className="h-3 w-3" />{T("左右拖动蓝色阶段条 = 该阶段及其后未完成阶段整体顺延 / 提前（松手后填一次原因）")}<button type="button" data-testid="dv3-gantt-shift-all" onClick={shiftAll} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto text-violet-700 font-semibold hover:underline">{T("整体顺延 N 天")}</button>{drag && dayShift !== 0 && <span className="text-red-600 font-bold" data-testid="dv3-gantt-drag-days">{dayShift > 0 ? "+" : ""}{dayShift}{T(" 天")}</span>}</div>}
      {stages.map((s, i) => {
        const dragging = drag?.id === s.id;
        const after = drag && !dragging && s.status !== "Completed" && i > stages.findIndex((x) => x.id === drag.id);
        const shift = (dragging || after) ? drag.dx : 0;
        return (
          <div key={s.id} className="flex-wrap md:flex-nowrap flex items-center gap-2 text-[10px]" data-testid={`dv3-gantt-row-${s.key}`}>
            <span className="w-36 truncate text-slate-600">{T(s.name)}</span>
            <div className="relative flex-1 h-4 bg-slate-50 rounded" ref={i === 0 ? trackRef : undefined}>
              {s.plannedStart && (s.baselineDue || s.dueDate) && <div className="absolute top-0 h-1.5 bg-slate-300 rounded" style={pos(s.plannedStart, s.baselineDue || s.dueDate)} />}
              {s.plannedStart && s.dueDate && <div data-testid={`dv3-gantt-bar-${s.key}`} onPointerDown={(e) => onDown(e, s)} className={`absolute top-1.5 h-1.5 rounded ${s.status === "Completed" ? "bg-emerald-400" : isManager ? "bg-indigo-400 cursor-ew-resize hover:bg-indigo-600 hover:h-2.5 hover:top-1" : "bg-indigo-400"} ${dragging ? "bg-indigo-700 h-2.5 top-1 shadow" : ""} ${after ? "opacity-60" : ""}`} style={{ ...pos(s.plannedStart, s.dueDate), transform: shift ? `translateX(${shift}px)` : undefined }} title={T("{0} · 截止 {1}", [T(s.name), d10(s.dueDate)])} />}
              {s.actualStart && <div className="absolute top-3 h-1 bg-emerald-500 rounded" style={pos(s.actualStart, s.actualFinish || new Date().toISOString())} />}
            </div>
            <span className="w-12 text-slate-400">{md(s.dueDate)}</span>
          </div>
        );
      })}
      <p className="text-[10px] text-slate-400">{T("灰 基线 · 蓝 当前截止 · 绿 实际{0}", [isManager ? T(" · 拖动蓝条重排计划") : ""])}</p>
    </div>
  );
};

export const AgenciesTab = ({ pid, meta, refresh }) => {
  const { api, lang } = useApp();
  const { d, load } = useLegacyDetail(pid);
  const [ai, setAi] = useState(false);
  const [edit, setEdit] = useState(null);
  // 机构行反向显示「关联产物」（映射由「产物时间设置 → 产物↔机构」配置）——
  // 这样在机构页签里也能一眼看出「这个机构是为了哪项交付物在跑」，两个页签互相对得上。
  const agMap = useDiscAgencyMap(api);
  const agToDisc = useMemo(() => reverseAgencyMap(agMap), [agMap]);
  /* 机构卡信息量比产物卡大（类型 + 适用范围 + 状态 + 关联产物 + 附件）→ 单卡 340px 起，最多 4 列 */
  const [agCols, bindAgGrid] = useGridCols(340, 4);
  const discName = (k) => { const x = (meta.disciplines || []).find((v) => v.key === k); return x ? T(x.name) : k; };
  if (!d) return <div className="py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (d.error) return <p className="text-sm text-red-600">{errTextOf(d.error)}</p>;
  const ags = d.agencies || [];
  const designers = meta.designers || [];
  const smap = meta.stageMap?.agencies || {};
  const stageDue = (type) => d.stages.find((s) => s.key === smap[type])?.dueDate;
  const onChanged = () => { load(); refresh?.(); };
  const save = async () => {
    if (edit.applicability === "Exempt/Not Applicable" && !(edit.basis || "").trim()) return toast.error(T("Exempt 必须填写豁免依据"));
    try { await api.patch(`/design/agencies/${edit.id}`, { applicability: edit.applicability, basis: edit.basis }); toast.success(T("已更新")); setEdit(null); onChanged(); } catch (e) { toast.error(e?.detailText || e.message); }
  };
  const applyAI = async (id, applicability, basis) => { await api.patch(`/design/agencies/${id}`, { applicability, basis: basis || /* raw */ "AI 建议" }); onChanged(); };
  const cls = (a) => a === "Required" ? "bg-red-100 text-red-700" : a === "Exempt/Not Applicable" ? "bg-slate-100 text-slate-500" : a === "Potentially Required" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400";
  const doneN = ags.filter((a) => AG_DONE.includes(a.status) || a.applicability === "Exempt/Not Applicable").length;
  return (
    <div className="space-y-3" data-testid="dv3-agencies">
      <div className="bg-white rounded-lg border border-slate-200 p-3 flex items-center gap-2 flex-wrap">
        <Hammer className="h-4 w-4 text-slate-500" /><p className="text-xs text-slate-600 flex-1">{T("专项审批研判矩阵：{0} 项必需 · {1} 项可能 · {2} 项豁免 · ", [ags.filter((a) => a.applicability === "Required").length, ags.filter((a) => a.applicability === "Potentially Required").length, ags.filter((a) => a.applicability === "Exempt/Not Applicable").length])}<b className="text-emerald-700" data-testid="dv3-agency-done-count">{T("已完成 {0}/{1}", [doneN, ags.length])}</b></p>
        {meta.canEdit && <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="dv3-agency-ai" onClick={() => setAi(true)}><Sparkles className="h-3.5 w-3.5 mr-1 text-violet-500" />{T("AI 研判")}</Button>}
      </div>
      <SubmittalPanel pid={pid} meta={meta} onChanged={onChanged} tid="dv3-submittals-all" />
      {ags.length === 0 && <p className="text-sm text-slate-400 text-center py-6">{T("暂无机构清单")}</p>}
      <div ref={bindAgGrid} className="grid gap-2 items-start" style={{ gridTemplateColumns: `repeat(${agCols}, minmax(0, 1fr))`, gridAutoFlow: "row dense" }} data-testid="dv3-agency-grid">
        {ags.map((a) => (
          <div key={a.id} className="space-y-1" data-testid={`dv3-agency-${a.id}`}>
            <AgencyRow a={{ ...a, dueDate: stageDue(a.type) }} meta={meta} api={api} designers={designers} onChanged={onChanged} docs={d.agencyDocs?.[a.type] || []} resp={d.agencyResp || {}} />
            <div className="flex items-center gap-2 px-1">
              <span className="text-[10px] text-slate-400 flex-1 truncate">{agToDisc[a.type] && <span className="text-violet-700 font-semibold" data-testid={`dv3-agency-disc-${a.id}`}>{T("关联产物")}：{discName(agToDisc[a.type])} · </span>}{a.basis || (smap[a.type] ? T("挂载阶段：{0}", [T(STAGE_SHORT[smap[a.type]] || smap[a.type])]) : "")}</span>
              {meta.canEdit && <button data-testid={`dv3-agency-edit-${a.id}`} onClick={() => setEdit(edit?.id === a.id ? null : { id: a.id, applicability: a.applicability || "Unknown", basis: a.basis || "" })} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[10px] px-2 py-0.5 rounded-full font-semibold ${cls(a.applicability)}`}>{T("{0} · 研判", [a.applicability || "Unknown"])}</button>}
            </div>
            {edit?.id === a.id && (
              <div className="mt-1 space-y-1.5 bg-white rounded-lg border border-slate-200 p-2" data-testid={`dv3-agency-form-${a.id}`}>
                <div className="flex flex-wrap gap-1">{APPLIC.map((x) => <button key={x} data-testid={`dv3-agency-opt-${a.id}-${x.replace(/[^A-Za-z]/g, "")}`} onClick={() => setEdit({ ...edit, applicability: x })} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`text-[10px] px-2 py-0.5 rounded-full border ${edit.applicability === x ? "ring-2 ring-slate-900 " + cls(x) : cls(x)}`)}>{x}</button>)}</div>
                <input data-testid={`dv3-agency-basis-${a.id}`} value={edit.basis} onChange={(e) => setEdit({ ...edit, basis: e.target.value })} placeholder={edit.applicability === "Exempt/Not Applicable" ? T("豁免依据（必填）") : T("依据 / 备注")} className="w-full text-xs border border-slate-200 rounded-md px-2 py-1" />
                <div className="flex-wrap md:flex-nowrap flex justify-end gap-1"><Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setEdit(null)}>{T("取消")}</Button><Button size="sm" className="h-6 text-xs bg-slate-900 hover:bg-slate-700" data-testid={`dv3-agency-save-${a.id}`} onClick={save}>{T("保存")}</Button></div>
              </div>
            )}
          </div>
        ))}
      </div>
      {ai && <AgencyAdviceModal pid={pid} api={api} zh lang={lang} agencies={ags} onApply={applyAI} onClose={() => setAi(false)} />}
    </div>
  );
};
